import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import tareoService from '../services/tareoService';
import ExcelJS from 'exceljs';
import { hasPermission, PERMISSIONS } from '../utils/permissions';
import { buscarHeaderTasaNocturna, parsearConsiderarTasaNocturnaExcel, prepararTareosImportacion } from '../utils/tareoTasaNocturna';
import { empleadoActivoDuranteRangoPlameOInterno, empleadoActivoEnFechaPlameOInterno, normalizarFechaImportacion, normalizarValorCeldaExcel } from '../utils/fechaImportacion';
import { claveUnicaTareo, describirTareoRegistrado, mapaClavesTareosExistentes } from '../utils/tareoDuplicado';
import {
  TAREO_EVENTO_CODIGOS,
  diasDelMes,
  letraDiaSemana,
  resolverTipoEventoDesdeCodigo,
} from '../utils/tareoEventoCodigos';
import configuracionViaticosStandardService, { expandirDestinosBoleta } from '../services/configuracionViaticosStandardService';
import configuracionPlanillaAlternativaService from '../services/configuracionPlanillaAlternativaService';

const TIPOS_EVENTO = [
  'asistio', 'falta', 'feriado', 'feriado_laborado', 'descanso', 'descanso_laborado',
  'descanso_medico', 'reposo_medico', 'subsidio_por_enfermedad', 'permiso', 'suspension', 'vacaciones'
];

// Eventos en los que el sistema conserva el teórico pero las horas reales deben ser 0 (vacías en Excel).
const TIPOS_EVENTO_HORAS_REALES_NULAS = [
  'falta', 'feriado', 'descanso', 'permiso', 'suspension', 'vacaciones',
  'descanso_medico', 'reposo_medico', 'subsidio_por_enfermedad'
];
// Feriado laborado / descanso laborado: el sistema los trata como asistió puntual (real = teórico si no se indica).
const TIPOS_EVENTO_REAL_IGUAL_TEORICO = ['feriado_laborado', 'descanso_laborado'];

const normalizarFecha = normalizarFechaImportacion;

const normalizarHora = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  const s = String(valor).trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(':').map(Number);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  if (/^\d{4}$/.test(s)) return `${s.slice(0, 2)}:${s.slice(2)}`;
  return null;
};

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const esHeaderDiaMes = (texto) => {
  const raw = String(texto ?? '').trim();
  if (!/^\d{1,2}$/.test(raw)) return false;
  const n = parseInt(raw, 10);
  return n >= 1 && n <= 31;
};

const clasificarSubcolViatico = (texto) => {
  const l = String(texto ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (l.includes('recibo')) return 'recibo';
  if (l.includes('monto') || l.includes('importe')) return 'monto';
  if (l.includes('destino')) return 'destino';
  return null;
};

/** Lee Viáticos por día (N° recibo / Monto / Destino).
 * Soporta: (1) a la derecha del tareo en la misma hoja, (2) sección debajo, (3) hoja separada.
 */
const parsearSeccionViaticos = (wb, maxDiaMes) => {
  const mapaVacio = { mapa: {}, errores: [], hojaEncontrada: false, colInicioVia: 0 };

  const normalizarNombreHoja = (name) => String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const hojas = wb.worksheets || [];
  const wsTareo = hojas.find((s) => ['tareo', 'tareos', 'calendario'].includes(normalizarNombreHoja(s.name)))
    || hojas[0];
  const wsViaticosSeparada = hojas.find((s) => {
    const n = normalizarNombreHoja(s.name);
    return n === 'viaticos' || n.includes('viatico');
  });

  const esMarcadorViaticos = (valor) => {
    const t = String(valor ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
    return t === 'viaticos' || t.startsWith('viaticos');
  };

  const parsearBloquesVia = (ws, colInicioMin, rowScanFrom, rowScanTo) => {
    const cellVal = (rowNum, colNum) => normalizarValorCeldaExcel(ws.getRow(rowNum).getCell(colNum)?.value);
    const maxScanCols = Math.max(colInicioMin + maxDiaMes * 3 + 5, 80);
    let rowDias = 0;
    let rowSub = 0;
    const bloques = [];

    /**
     * ExcelJS (al releer xlsx) repite el valor del merge en todas las celdas del grupo.
     * Ej: día 1 en cols 34-36 aparece como 1,1,1 → hay que colapsar a la 1ª columna del grupo.
     */
    const colapsarDiasMerge = (diasRaw) => {
      const colapsados = [];
      diasRaw.forEach((d) => {
        const last = colapsados[colapsados.length - 1];
        if (!last || last.dia !== d.dia) colapsados.push({ col: d.col, dia: d.dia });
      });
      const vistos = new Set();
      return colapsados.filter((d) => {
        if (vistos.has(d.dia)) return false;
        vistos.add(d.dia);
        return true;
      });
    };

    for (let r = rowScanFrom; r <= rowScanTo; r++) {
      const diasRaw = [];
      for (let c = colInicioMin; c <= maxScanCols; c++) {
        const v = cellVal(r, c);
        if (esHeaderDiaMes(v)) {
          const dia = parseInt(String(v).trim(), 10);
          if (dia >= 1 && dia <= maxDiaMes) diasRaw.push({ col: c, dia });
        }
      }
      const diasEnFila = colapsarDiasMerge(diasRaw);
      // Evitar tomar la franja de tareo (1 col/día): viáticos suelen tener span >= 2 o subcols debajo
      if (diasEnFila.length >= 7) {
        const spanEst = diasEnFila.length >= 2
          ? diasEnFila[1].col - diasEnFila[0].col
          : 1;
        const filaDebajo = r + 1;
        let hitsSub = 0;
        for (let c = colInicioMin; c <= maxScanCols; c++) {
          if (clasificarSubcolViatico(cellVal(filaDebajo, c))) hitsSub += 1;
        }
        // Horizontal: días con span>1, o hay subcols recibo/monto debajo
        // Vertical legacy en hoja aparte: span puede ser 1 con subcols, o solo monto
        if (spanEst >= 2 || hitsSub >= 1 || colInicioMin > 3) {
          rowDias = r;
          const span = Math.max(1, spanEst);
          diasEnFila.forEach((d) => {
            bloques.push({
              dia: d.dia,
              startCol: d.col,
              endCol: d.col + span - 1,
              cols: {},
            });
          });
          rowSub = hitsSub >= 1 ? filaDebajo : r;
          break;
        }
      }
    }

    if (!rowDias || !bloques.length) return null;

    // Ajustar fila de subcolumnas
    for (let r = rowDias; r <= Math.min(rowDias + 2, ws.rowCount || 0); r++) {
      let hits = 0;
      for (let c = colInicioMin; c <= maxScanCols; c++) {
        if (clasificarSubcolViatico(cellVal(r, c))) hits += 1;
      }
      if (hits >= 1) {
        rowSub = r;
        break;
      }
    }

    // Mapa offset→tipo desde el primer día bien etiquetado (recibo/monto/destino)
    const offsetsPorTipo = {};
    bloques.forEach((b) => {
      for (let c = b.startCol; c <= b.endCol; c++) {
        const tipo = clasificarSubcolViatico(cellVal(rowSub, c));
        if (tipo) {
          b.cols[tipo] = c;
          if (offsetsPorTipo[tipo] === undefined) {
            offsetsPorTipo[tipo] = c - b.startCol;
          }
        }
      }
    });

    // Propagar offsets a todos los días (por si algún grupo perdió encabezados)
    if (Object.keys(offsetsPorTipo).length > 0) {
      bloques.forEach((b) => {
        Object.entries(offsetsPorTipo).forEach(([tipo, off]) => {
          if (!b.cols[tipo]) b.cols[tipo] = b.startCol + off;
        });
      });
    }

    bloques.forEach((b) => {
      if (!b.cols.monto) {
        const libres = [];
        for (let c = b.startCol; c <= b.endCol; c++) {
          if (c !== b.cols.recibo && c !== b.cols.destino) libres.push(c);
        }
        if (libres.length === 1) b.cols.monto = libres[0];
        else if (!b.cols.recibo && !b.cols.destino) b.cols.monto = b.startCol;
      }
    });

    // DNI: normalmente columna 1 de la hoja (compartida con tareo)
    let colDni = 1;
    for (let c = 1; c <= 5; c++) {
      const l = String(cellVal(rowSub, c) ?? '').toLowerCase();
      if (l === 'dni' || l.includes('documento')) {
        colDni = c;
        break;
      }
    }

    return { bloques, rowSub, colDni, cellVal, colInicioVia: colInicioMin };
  };

  const leerDatos = (ws, meta) => {
    const localMapa = {};
    const { bloques, rowSub, colDni, cellVal } = meta;
    for (let rowNumber = rowSub + 1; rowNumber <= (ws.rowCount || 0); rowNumber++) {
      const dni = String(cellVal(rowNumber, colDni) ?? '').trim();
      if (!dni) continue;
      if (/destino:|deje vac|leyenda|codigo|código/i.test(dni)) continue;
      if (esMarcadorViaticos(dni)) continue;
      if (String(dni).toLowerCase() === 'dni') continue;

      bloques.forEach((b) => {
        const colMonto = b.cols.monto;
        if (!colMonto) return;
        const monto = parseFloat(cellVal(rowNumber, colMonto));
        if (isNaN(monto) || monto <= 0) return;
        const recibo = b.cols.recibo ? String(cellVal(rowNumber, b.cols.recibo) ?? '').trim() : '';
        const destino = b.cols.destino ? String(cellVal(rowNumber, b.cols.destino) ?? '').trim() : '';
        if (!localMapa[dni]) localMapa[dni] = {};
        localMapa[dni][b.dia] = { monto, recibo, destino, fila: rowNumber };
      });
    }
    return localMapa;
  };

  // 1) Misma hoja: banner VIATICOS a la derecha (o debajo)
  if (wsTareo) {
    const cellVal0 = (r, c) => normalizarValorCeldaExcel(wsTareo.getRow(r).getCell(c)?.value);
    const maxCols = Math.max(2 + maxDiaMes * 4, 100);
    let rowTitulo = 0;
    let colInicioVia = 0;
    for (let r = 1; r <= Math.min(8, wsTareo.rowCount || 1); r++) {
      for (let c = 1; c <= maxCols; c++) {
        if (esMarcadorViaticos(cellVal0(r, c))) {
          rowTitulo = r;
          colInicioVia = c;
          break;
        }
      }
      if (colInicioVia) break;
    }

    if (colInicioVia) {
      const meta = parsearBloquesVia(
        wsTareo,
        colInicioVia,
        rowTitulo,
        Math.min(rowTitulo + 5, wsTareo.rowCount || 1)
      );
      if (meta) {
        return {
          mapa: leerDatos(wsTareo, meta),
          errores: [],
          hojaEncontrada: true,
          colInicioVia,
        };
      }
      return {
        mapa: {},
        errores: [{
          fila: rowTitulo,
          nombre: 'Viaticos',
          mensaje: 'Sección Viaticos inválida: se esperan números de día a la derecha del tareo.',
        }],
        hojaEncontrada: true,
        colInicioVia,
      };
    }
  }

  // 2) Hoja separada "Viaticos" (compatibilidad)
  if (wsViaticosSeparada && wsViaticosSeparada !== wsTareo) {
    const meta = parsearBloquesVia(wsViaticosSeparada, 1, 1, Math.min(8, wsViaticosSeparada.rowCount || 1));
    if (meta) {
      return {
        mapa: leerDatos(wsViaticosSeparada, meta),
        errores: [],
        hojaEncontrada: true,
        colInicioVia: 0,
      };
    }
  }

  return mapaVacio;
};

const COLUMNA_TASA_NOCTURNA_LEYENDA = [
  'Considerar tasa nocturna',
  'Indica si aplica recargo nocturno en ese día',
  'Si o No (vacío = No)',
];

const COLUMNA_TASA_NOCTURNA_HEADER = 'Considerar tasa nocturna';

const insertarColumnaTasaNocturnaEnLeyenda = (columnas, activa) => {
  if (!activa) return columnas;
  const copia = [...columnas];
  const idx = copia.findIndex(([nombre]) => nombre === 'Concepto');
  copia.splice(idx >= 0 ? idx : copia.length, 0, COLUMNA_TASA_NOCTURNA_LEYENDA);
  return copia;
};

const construirHeadersPlantilla = (esSimple, activaTasaNocturna, conDestinoBoleta = false, conSede = false) => {
  const base = esSimple
    ? ['DNI', 'Fecha', 'Tipo evento', 'Observaciones']
    : ['DNI', 'Fecha', 'Tipo evento', 'Hora entrada teórica', 'Hora salida teórica', 'Hora entrada real', 'Hora salida real', 'Observaciones'];

  if (conSede) {
    base.splice(1, 0, 'Proyecto');
  }

  if (activaTasaNocturna) {
    base.push(COLUMNA_TASA_NOCTURNA_HEADER);
  }

  base.push('Concepto', 'Monto concepto');
  if (conDestinoBoleta) {
    base.push('Destino boleta');
  }
  return base;
};

const ImportadorTareos = ({ onClose, onImportSuccess, selectedCompany, selectedProject = null, projects = [], employees = [], plameConceptos = [], initialMode = 'importar', esTareoSimple = false, tasaNocturnaActiva = false }) => {
  const now = new Date();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth()); // 0-11
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [archivo, setArchivo] = useState(null);
  const [procesandoArchivo, setProcesandoArchivo] = useState(false);
  const [datosExcel, setDatosExcel] = useState([]);
  const [erroresValidacion, setErroresValidacion] = useState([]);
  const [filasValidas, setFilasValidas] = useState([]);
  const [filasYaExistentes, setFilasYaExistentes] = useState([]);
  const [consultandoDuplicados, setConsultandoDuplicados] = useState(false);
  const [mostrarValidaciones, setMostrarValidaciones] = useState(false);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const puedeAprobarPermisos = hasPermission(user, PERMISSIONS.APPROVE_PAYROLLS);
  const [viaticosActivo, setViaticosActivo] = useState(false);
  const [viaticosConfig, setViaticosConfig] = useState(null);
  const [planillaAlternativaActiva, setPlanillaAlternativaActiva] = useState(false);
  const numeroReciboActivo = Boolean(viaticosActivo && viaticosConfig?.numero_recibo_activo);

  // Modo: 'importar' (crear tareos) o 'regularizar' (solo añadir conceptos a tareos existentes)
  // Sin sede fija: importación multi-sede (resuelve proyecto por empleado/columna) o solo regularizar global
  const esMultiSede = !selectedProject;
  const soloRegularizarGlobal = esMultiSede && initialMode === 'regularizar';
  const [modoImportacion, setModoImportacion] = useState(
    soloRegularizarGlobal || initialMode === 'regularizar' ? 'regularizar' : 'importar'
  );
  const fileInputRefRegularizar = useRef(null);
  const [archivoRegularizar, setArchivoRegularizar] = useState(null);
  const [procesandoRegularizar, setProcesandoRegularizar] = useState(false);
  const [datosRegularizar, setDatosRegularizar] = useState([]);
  const [erroresRegularizar, setErroresRegularizar] = useState([]);
  const [filasValidasRegularizar, setFilasValidasRegularizar] = useState([]);
  const [mostrarValidacionesRegularizar, setMostrarValidacionesRegularizar] = useState(false);
  const [aplicandoRegularizar, setAplicandoRegularizar] = useState(false);
  const [resultadoRegularizar, setResultadoRegularizar] = useState(null);
  const tasaNocturnaActivaAnteriorRef = useRef(tasaNocturnaActiva);

  useEffect(() => {
    const activoRecienCargado = tasaNocturnaActiva && !tasaNocturnaActivaAnteriorRef.current;
    tasaNocturnaActivaAnteriorRef.current = tasaNocturnaActiva;

    if (activoRecienCargado && archivo && modoImportacion === 'importar' && !procesandoArchivo) {
      procesarArchivo(archivo);
    }
  }, [tasaNocturnaActiva, archivo, modoImportacion, procesandoArchivo]);

  useEffect(() => {
    let cancelled = false;
    const cargarFlags = async () => {
      if (!selectedCompany?.id) {
        setViaticosActivo(false);
        setViaticosConfig(null);
        setPlanillaAlternativaActiva(false);
        return;
      }
      try {
        const respVia = await configuracionViaticosStandardService.obtenerConfiguracion(selectedCompany.id);
        if (!cancelled) {
          const esFlagActivo = (v) => v === true || v === 1 || v === '1' || v === 'true';
          const viaOk = Boolean(respVia?.success && (esFlagActivo(respVia?.data?.activo) || esFlagActivo(respVia?.data?.viaticos_standard_activo)));
          setViaticosActivo(viaOk);
          setViaticosConfig(respVia?.success ? (respVia.data || null) : null);
        }
      } catch (_) {
        if (!cancelled) {
          setViaticosActivo(false);
          setViaticosConfig(null);
        }
      }
      try {
        const respPa = await configuracionPlanillaAlternativaService.verificarEstado(selectedCompany.id);
        if (!cancelled) {
          setPlanillaAlternativaActiva(
            Boolean(respPa?.success && (respPa?.data?.planilla_alternativa_activa || respPa?.data?.activo))
          );
        }
      } catch (_) {
        if (!cancelled) setPlanillaAlternativaActiva(false);
      }
    };
    cargarFlags();
    const onViaAtualizada = () => { cargarFlags(); };
    window.addEventListener('configuracion-viaticos-standard-actualizada', onViaAtualizada);
    return () => {
      cancelled = true;
      window.removeEventListener('configuracion-viaticos-standard-actualizada', onViaAtualizada);
    };
  }, [selectedCompany?.id]);

  /** Lee config vigente de viáticos/PA justo antes de armar la plantilla (evita estado stale). */
  const resolverFlagsPlantilla = async () => {
    const esFlagActivo = (v) => v === true || v === 1 || v === '1' || v === 'true';
    let viaActivo = viaticosActivo;
    let viaCfg = viaticosConfig;
    let paActiva = planillaAlternativaActiva;
    if (!selectedCompany?.id) {
      return { viaActivo, viaCfg, paActiva, numeroRecibo: Boolean(viaActivo && viaCfg?.numero_recibo_activo) };
    }
    try {
      const respVia = await configuracionViaticosStandardService.obtenerConfiguracion(selectedCompany.id);
      if (respVia?.success && respVia.data) {
        viaActivo = esFlagActivo(respVia.data.activo) || esFlagActivo(respVia.data.viaticos_standard_activo);
        viaCfg = respVia.data;
        setViaticosActivo(viaActivo);
        setViaticosConfig(viaCfg);
      }
    } catch (_) { /* keep state */ }
    try {
      const respPa = await configuracionPlanillaAlternativaService.verificarEstado(selectedCompany.id);
      if (respPa?.success) {
        paActiva = esFlagActivo(respPa.data?.planilla_alternativa_activa) || esFlagActivo(respPa.data?.activo);
        setPlanillaAlternativaActiva(paActiva);
      }
    } catch (_) { /* keep state */ }
    return {
      viaActivo,
      viaCfg,
      paActiva,
      numeroRecibo: Boolean(viaActivo && esFlagActivo(viaCfg?.numero_recibo_activo)),
    };
  };

  // Empleados del proyecto (o de todas las sedes visibles) activos en el mes/año
  const empleadosImportables = React.useMemo(() => {
    if (!employees.length) return [];
    const firstDay = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
    const lastDayNum = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const lastDay = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(lastDayNum).padStart(2, '0')}`;
    const delProyecto = selectedProject
      ? employees.filter(emp => {
          const proy = (emp.proyecto || '').toString().trim();
          const proyId = emp.proyecto_id != null ? String(emp.proyecto_id) : '';
          return proy === (selectedProject.nombre || '').trim() || proyId === String(selectedProject.id);
        })
      : (projects?.length
        ? employees.filter((emp) => {
            const proy = (emp.proyecto || '').toString().trim();
            const proyId = emp.proyecto_id != null ? String(emp.proyecto_id) : '';
            return projects.some(
              (p) => proy === (p.nombre || '').trim() || proyId === String(p.id)
            );
          })
        : employees);
    return delProyecto.filter(emp => {
      return empleadoActivoDuranteRangoPlameOInterno(emp, firstDay, lastDay);
    });
  }, [employees, selectedProject, projects, selectedMonth, selectedYear]);

  const resolverProyectoId = (emp, sedeExcel = '') => {
    if (selectedProject?.id) return selectedProject.id;
    const sedeRaw = String(sedeExcel || '').trim();
    if (sedeRaw && projects?.length) {
      const porNombre = projects.find(
        (p) => String(p.nombre || '').trim().toLowerCase() === sedeRaw.toLowerCase()
      );
      if (porNombre) return porNombre.id;
      const porId = projects.find((p) => String(p.id) === sedeRaw);
      if (porId) return porId.id;
    }
    if (emp?.proyecto_id != null && emp.proyecto_id !== '') return Number(emp.proyecto_id) || emp.proyecto_id;
    if (emp?.proyecto && projects?.length) {
      const p = projects.find(
        (pr) => String(pr.nombre || '').trim().toLowerCase() === String(emp.proyecto).trim().toLowerCase()
      );
      if (p) return p.id;
    }
    return null;
  };

  const empleadoPorDni = (dni) => {
    const d = String(dni ?? '').trim();
    return empleadosImportables.find(e => String(e.numero_documento ?? '').trim() === d);
  };

  const empleadoActivoEnFecha = empleadoActivoEnFechaPlameOInterno;

  const obtenerHorarioTeoricoEmpleado = (empleado) => ({
    entrada: normalizarHora(empleado?.hora_entrada_formatted || empleado?.hora_entrada) || '08:00',
    salida: normalizarHora(empleado?.hora_salida_formatted || empleado?.hora_salida) || '17:00',
  });

  // Alinear tipo con PLAME: clasificación 0700 (códigos 07xx) son descuentos, no "otro"
  const tipoConceptoParaTareo = (c) => {
    const cod = String(c.codigo_plame || c.codigo || '').trim();
    const tipo = c.tipo_concepto;
    if (tipo === 'otro' && /^07\d{2}$/.test(cod)) return 'descuento';
    return tipo;
  };

  // Resolver concepto por código PLAME o por nombre (id)
  const resolverConcepto = (valor) => {
    if (valor === null || valor === undefined || String(valor).trim() === '') return null;
    const v = String(valor).trim();
    const porCodigo = plameConceptos.find(c => String(c.codigo || '').trim() === v);
    if (porCodigo) return { id: porCodigo.id, tipo_concepto: tipoConceptoParaTareo(porCodigo) };
    const porNombre = plameConceptos.find(c => (c.nombre || '').toLowerCase() === v.toLowerCase());
    if (porNombre) return { id: porNombre.id, tipo_concepto: tipoConceptoParaTareo(porNombre) };
    const porId = plameConceptos.find(c => String(c.id) === v);
    if (porId) return { id: porId.id, tipo_concepto: tipoConceptoParaTareo(porId) };
    return null;
  };

  const procesarArchivo = async (file) => {
    setProcesandoArchivo(true);
    setDatosExcel([]);
    setErroresValidacion([]);
    setFilasValidas([]);
    setFilasYaExistentes([]);
    setMostrarValidaciones(false);
    setResultado(null);
    try {
      const buffer = await file.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buffer);
      const ws =
        wb.worksheets.find((s) => ['tareos', 'tareo', 'calendario'].includes(String(s.name || '').toLowerCase()))
        || wb.worksheets[0];
      if (!ws) {
        setErroresValidacion([{ fila: 0, nombre: '-', mensaje: 'No se encontró la hoja "Tareos" / "Tareo" en el archivo.' }]);
        setMostrarValidaciones(true);
        setProcesandoArchivo(false);
        return;
      }

      // Detectar fila de encabezados (formato largo o calendario)
      let headerRowNumber = 1;
      let headers = [];
      for (let r = 1; r <= Math.min(8, ws.rowCount || 1); r++) {
        const row = ws.getRow(r);
        const vals = [];
        row.eachCell((cell, colNumber) => {
          vals[colNumber - 1] = String(normalizarValorCeldaExcel(cell.value) ?? '').trim();
        });
        const lower = vals.map((v) => String(v || '').toLowerCase());
        const tieneDni = lower.some((v) => v === 'dni' || v.includes('documento'));
        const tieneFecha = lower.some((v) => v === 'fecha');
        const tieneTipo = lower.some((v) => v.includes('tipo') && v.includes('evento'));
        const diasCols = vals.filter((v) => esHeaderDiaMes(v)).length;
        if (tieneDni && (tieneFecha || tieneTipo || diasCols >= 28)) {
          headerRowNumber = r;
          headers = vals;
          break;
        }
      }

      if (!headers.length) {
      const headerRow = ws.getRow(1);
        headerRow.eachCell((cell, colNumber) => {
          headers[colNumber - 1] = String(normalizarValorCeldaExcel(cell.value) ?? '').trim();
        });
        headerRowNumber = 1;
      }

      const headerLower = headers.map((h) => String(h || '').toLowerCase());
      const diasHeaderCount = headers.filter((h) => esHeaderDiaMes(h)).length;
      const esFormatoCalendario =
        diasHeaderCount >= 28
        || (diasHeaderCount >= 7
          && headerLower.some((h) => h === 'dni' || h.includes('documento'))
          && !headerLower.includes('fecha')
          && !headerLower.some((h) => h.includes('tipo') && h.includes('evento')));

      const headerTasaNocturna = buscarHeaderTasaNocturna(headers);
      const getVal = (obj, keys) => {
        for (const k of keys) {
          const v = normalizarValorCeldaExcel(obj[k]);
          if (v !== undefined && v !== null && v !== '') return v;
        }
        return '';
      };

      let limpio = [];

      if (esFormatoCalendario) {
        const colDniIdx = headers.findIndex((h) => {
          const l = String(h || '').toLowerCase();
          return l === 'dni' || l.includes('documento') || l === 'n°' || l === 'nº';
        });
        const colSedeIdx = headers.findIndex((h) => {
          const l = String(h || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return l === 'sede' || l === 'proyecto' || l.includes('sede') || l.includes('proyecto');
        });
        // Compatibilidad plantillas antiguas: columnas al final de la hoja Tareo
        const colViaticoIdx = headers.findIndex((h) => {
          const l = String(h || '').toLowerCase();
          return l.includes('viatico') || l.includes('viático') || l.includes('monto viatico') || l.includes('monto viático');
        });
        const colReciboIdx = headers.findIndex((h) => {
          const l = String(h || '').toLowerCase();
          return l.includes('recibo') || l.includes('n° recibo') || l.includes('nº recibo') || l === 'nro recibo';
        });
        const colDestinoIdx = headers.findIndex((h) => {
          const l = String(h || '').toLowerCase();
          // Evitar confundir "Destino boleta" de plantilla por filas; en calendario Destino está en VIATICOS
          return l === 'destino' || (l.includes('destino') && !l.includes('boleta'));
        });
        const diasColsAll = [];
        headers.forEach((h, idx) => {
          if (esHeaderDiaMes(h)) {
            diasColsAll.push({ col: idx, dia: parseInt(String(h).trim(), 10) });
          }
        });

        if (colDniIdx < 0 || diasColsAll.length === 0) {
          setErroresValidacion([{
            fila: 0,
            nombre: '-',
            mensaje: 'Plantilla calendario inválida: se requiere columna DNI y columnas de días (1…N del mes).',
          }]);
          setMostrarValidaciones(true);
          setProcesandoArchivo(false);
          return;
        }

        const mes = selectedMonth + 1;
        const anio = selectedYear;
        const maxDiaMes = diasDelMes(anio, selectedMonth);

        // Viáticos a la derecha (o legacy abajo / hoja aparte)
        const parseVia = viaticosActivo
          ? parsearSeccionViaticos(wb, maxDiaMes)
          : { mapa: {}, errores: [], hojaEncontrada: false, colInicioVia: 0 };
        const viaticosPorDniDia = parseVia.mapa || {};
        const erroresPreviosVia = parseVia.errores || [];
        const colInicioVia0 = parseVia.colInicioVia > 0 ? parseVia.colInicioVia - 1 : -1; // 0-based

        // Solo días del tareo (antes de la franja VIATICOS); primera secuencia 1..N
        let diasCols = diasColsAll;
        if (colInicioVia0 >= 0) {
          diasCols = diasColsAll.filter((d) => d.col < colInicioVia0);
        }
        // Si hay días duplicados (1..N dos veces), quedarse con la primera corrida
        const vistos = new Set();
        diasCols = diasCols.filter((d) => {
          if (vistos.has(d.dia)) return false;
          vistos.add(d.dia);
          return true;
        });

        // Formato antiguo: un solo monto por fila al final de Tareo
        const viaticoLegacyPorDni = {};
        const diasConTareo = {};

        for (let rowNumber = headerRowNumber + 1; rowNumber <= ws.rowCount; rowNumber++) {
          const row = ws.getRow(rowNumber);
          const obj = {};
          row.eachCell((cell, colNumber) => {
            obj[colNumber - 1] = normalizarValorCeldaExcel(cell.value);
          });
          const dni = String(obj[colDniIdx] ?? '').trim();
          if (!dni) continue;
          if (/leyenda|código|codigo|destino:/i.test(dni)) continue;
          if (String(dni).toLowerCase() === 'dni') continue;
          const tMarc = String(dni).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          if (tMarc === 'viaticos' || tMarc.startsWith('viaticos')) continue;
          const sedeFila = colSedeIdx >= 0 ? String(obj[colSedeIdx] ?? '').trim() : '';

          if (viaticosActivo && !parseVia.hojaEncontrada && colViaticoIdx >= 0) {
            const montoVia = parseFloat(obj[colViaticoIdx]);
            const recibo = colReciboIdx >= 0 ? String(obj[colReciboIdx] ?? '').trim() : '';
            const destinoRaw = colDestinoIdx >= 0 ? String(obj[colDestinoIdx] ?? '').trim() : '';
            if (!isNaN(montoVia) && montoVia > 0) {
              viaticoLegacyPorDni[dni] = { monto: montoVia, recibo, destino: destinoRaw, fila: rowNumber };
            }
          }

          let primerDiaIdx = -1;
          diasCols.forEach(({ col, dia }) => {
            if (dia < 1 || dia > maxDiaMes) return;
            const tipoRaw = obj[col];
            const tipo = resolverTipoEventoDesdeCodigo(tipoRaw);
            if (!tipo) return;
            const fecha = `${anio}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
            const esPrimero = primerDiaIdx < 0;
            if (esPrimero) primerDiaIdx = limpio.length;
            diasConTareo[`${dni}|${dia}`] = true;
            const viaDia = viaticosPorDniDia[dni]?.[dia] || null;
            limpio.push({
              dni,
              sede: sedeFila,
              fecha,
              tipo_evento: tipo,
              hora_entrada_teorica: '',
              hora_salida_teorica: '',
              hora_entrada_real: '',
              hora_salida_real: '',
              observaciones: '',
              considerar_tasa_nocturna: '',
              concepto: '',
              monto_concepto: '',
              destino_boleta: viaDia?.destino || '',
              _filaOrigen: viaDia?.fila || rowNumber,
              _formato: 'calendario',
              ...(viaDia ? { _viatico: viaDia } : {}),
            });
          });

          if (viaticoLegacyPorDni[dni] && primerDiaIdx >= 0 && limpio[primerDiaIdx] && !limpio[primerDiaIdx]._viatico) {
            limpio[primerDiaIdx]._viatico = viaticoLegacyPorDni[dni];
            limpio[primerDiaIdx].destino_boleta = viaticoLegacyPorDni[dni].destino || '';
          }
        }

        const erroresViaticosOrfanos = [];
        Object.entries(viaticosPorDniDia).forEach(([dni, porDia]) => {
          Object.entries(porDia).forEach(([diaStr, via]) => {
            const dia = Number(diaStr);
            if (!diasConTareo[`${dni}|${dia}`]) {
              erroresViaticosOrfanos.push({
                fila: via.fila || 0,
                nombre: dni,
                mensaje: `Viáticos: hay monto el día ${dia} pero no hay código de evento ese día en Tareo.`,
              });
            }
          });
        });

        if (erroresPreviosVia.length || erroresViaticosOrfanos.length) {
          limpio = limpio.slice();
          limpio.__erroresViaticos = [...erroresPreviosVia, ...erroresViaticosOrfanos];
        }
      } else {
      const rows = [];
        for (let rowNumber = headerRowNumber + 1; rowNumber <= ws.rowCount; rowNumber++) {
        const row = ws.getRow(rowNumber);
        const obj = {};
          row.eachCell((cell, colNumber) => {
            obj[headers[colNumber - 1]] = normalizarValorCeldaExcel(cell.value);
          });
        const dni = getVal(obj, ['DNI', 'dni', 'Número Documento']);
          if (dni === '' && Object.values(obj).every((v) => v === undefined || v === null || v === '')) continue;
          const valorTasaNocturna = headerTasaNocturna
            ? normalizarValorCeldaExcel(obj[headerTasaNocturna])
            : getVal(obj, ['Considerar tasa nocturna', 'considerar_tasa_nocturna', 'Tasa nocturna']);
        rows.push({
          dni,
            sede: getVal(obj, ['Sede', 'sede', 'Proyecto', 'proyecto']),
          fecha: getVal(obj, ['Fecha', 'fecha']),
          tipo_evento: getVal(obj, ['Tipo evento', 'tipo_evento', 'Tipo']),
            hora_entrada_teorica: esTareoSimple ? '' : (getVal(obj, ['Hora entrada teórica', 'hora_entrada_teorica', 'Hora entrada']) || '08:00'),
            hora_salida_teorica: esTareoSimple ? '' : (getVal(obj, ['Hora salida teórica', 'hora_salida_teorica', 'Hora salida']) || '17:00'),
            hora_entrada_real: esTareoSimple ? '' : getVal(obj, ['Hora entrada real', 'hora_entrada_real']),
            hora_salida_real: esTareoSimple ? '' : getVal(obj, ['Hora salida real', 'hora_salida_real']),
          observaciones: getVal(obj, ['Observaciones', 'observaciones']),
            considerar_tasa_nocturna: valorTasaNocturna,
          concepto: getVal(obj, ['Concepto', 'concepto']),
          monto_concepto: getVal(obj, ['Monto concepto', 'monto_concepto']),
          destino_boleta: getVal(obj, ['Destino boleta', 'destino_boleta', 'Destino', 'destino']),
          _filaOrigen: rowNumber,
        });
      }
      limpio = rows.map((r) => ({
        dni: String(r.dni ?? '').trim(),
        sede: String(r.sede ?? '').trim(),
        fecha: r.fecha,
        tipo_evento: resolverTipoEventoDesdeCodigo(r.tipo_evento) || String(r.tipo_evento ?? '').trim().toLowerCase().replace(/\s+/g, '_'),
        hora_entrada_teorica: r.hora_entrada_teorica ?? '',
        hora_salida_teorica: r.hora_salida_teorica ?? '',
        hora_entrada_real: r.hora_entrada_real ?? '',
        hora_salida_real: r.hora_salida_real ?? '',
        observaciones: String(r.observaciones ?? '').trim(),
        considerar_tasa_nocturna: r.considerar_tasa_nocturna,
        concepto: String(r.concepto ?? '').trim(),
        monto_concepto: r.monto_concepto,
        destino_boleta: String(r.destino_boleta ?? '').trim(),
        _filaOrigen: r._filaOrigen,
      }));
      }

      const erroresExtra = limpio.__erroresViaticos || [];
      if (limpio.__erroresViaticos) delete limpio.__erroresViaticos;
      setDatosExcel(limpio);
      await validarDatos(limpio, erroresExtra);
    } catch (err) {
      setErroresValidacion([{ fila: 0, nombre: '-', mensaje: 'Error al leer el Excel: ' + (err.message || 'archivo inválido') }]);
      setMostrarValidaciones(true);
    }
    setProcesandoArchivo(false);
  };

  const validarDatos = async (filas, erroresExtra = []) => {
    const errores = [...(erroresExtra || [])];
    const validas = [];
    const clavesEnArchivo = new Map();
    const codigosAyuda = TAREO_EVENTO_CODIGOS.map((c) => `${c.codigo}=${c.nombre}`).join(', ');
    filas.forEach((fila, idx) => {
      const nFila = fila._filaOrigen || (idx + 2);
      const errFila = [];
      const dni = String(fila.dni ?? '').trim();
      if (!dni) errFila.push('DNI es obligatorio');
      const emp = empleadoPorDni(dni);
      if (dni && !emp) errFila.push(esMultiSede
        ? 'No se encontró empleado activo con este DNI en los proyectos/mes seleccionado'
        : 'No se encontró empleado activo con este DNI en el proyecto/mes seleccionado');
      const fecha = normalizarFecha(fila.fecha);
      if (!fecha) errFila.push('Fecha inválida o vacía (use YYYY-MM-DD o formato de fecha válido)');
      if (emp && fecha && !empleadoActivoEnFecha(emp, fecha)) errFila.push('El empleado no estaba activo en esta fecha según su historial laboral');
      const proyectoIdResuelto = resolverProyectoId(emp, fila.sede);
      if (emp && !proyectoIdResuelto) {
        errFila.push(esMultiSede
          ? 'No se pudo determinar el proyecto (complete columna Proyecto o asigne proyecto al empleado)'
          : 'Proyecto no determinado');
      }
      const tipo = fila.tipo_evento;
      if (!tipo) errFila.push('Tipo de evento es obligatorio');
      if (tipo && !TIPOS_EVENTO.includes(tipo)) {
        errFila.push(
          fila._formato === 'calendario'
            ? `Código de evento no válido ("${tipo}"). Use: ${codigosAyuda}`
            : `Tipo de evento no válido. Use códigos (${codigosAyuda}) o: ${TIPOS_EVENTO.join(', ')}`
        );
      }

      const horarioEmp = emp ? obtenerHorarioTeoricoEmpleado(emp) : { entrada: '08:00', salida: '17:00' };
      const he = esTareoSimple
        ? horarioEmp.entrada
        : (normalizarHora(fila.hora_entrada_teorica) || '08:00');
      const hs = esTareoSimple
        ? horarioEmp.salida
        : (normalizarHora(fila.hora_salida_teorica) || '17:00');

      if (!esTareoSimple) {
      if (!he) errFila.push('Hora entrada teórica inválida');
      if (!hs) errFila.push('Hora salida teórica inválida');
      }
      const heReal = normalizarHora(fila.hora_entrada_real);
      const hsReal = normalizarHora(fila.hora_salida_real);
      if (!esTareoSimple && tipo && TIPOS_EVENTO_HORAS_REALES_NULAS.includes(tipo) && (heReal || hsReal)) {
        errFila.push(`Para el tipo de evento "${tipo}" las horas reales deben estar vacías (el sistema conserva el teórico y registra real en 0).`);
      }
      // Concepto opcional por fila (+ viático desde calendario)
      let conceptosPayload = [];
      const resolverDestinosFila = () => {
        if (!planillaAlternativaActiva) return ['plame'];
        const raw = String(fila.destino_boleta || '').trim().toLowerCase();
        if (raw === 'ambas' || raw === 'ambos' || raw === 'plame+interna') return ['plame', 'interna'];
        if (raw === 'interna' || raw === 'interno') return ['interna'];
        if (raw === 'plame') return ['plame'];
        // Vacío: default PLAME (o el de config de viáticos más abajo)
        return ['plame'];
      };

      if (fila.concepto) {
        const res = resolverConcepto(fila.concepto);
        if (!res) errFila.push('Concepto no reconocido (use código PLAME o nombre del concepto)');
        const monto = parseFloat(fila.monto_concepto);
        if (isNaN(monto) || monto <= 0) errFila.push('Monto concepto debe ser un número mayor a 0');
        if (res && !isNaN(monto) && monto > 0) {
          const destinos = resolverDestinosFila();
          conceptosPayload = destinos.map((tb) => ({
            plame_configuracion_id: res.id,
            tipo_concepto: res.tipo_concepto,
            tipo_boleta: tb,
            monto,
            observaciones: null,
          }));
        }
      } else if (fila.monto_concepto && parseFloat(fila.monto_concepto) > 0) {
        errFila.push('Si indica monto concepto debe indicar también el concepto');
      }

      if (fila._viatico && viaticosActivo) {
        const plameId = viaticosConfig?.plame_configuracion_id;
        if (!plameId) {
          errFila.push('Hay monto de viático pero no hay código PLAME configurado en Viáticos standard');
        } else {
          const pc = (plameConceptos || []).find((c) => Number(c.id) === Number(plameId));
          const tipoConcepto = pc ? tipoConceptoParaTareo(pc) : 'ingreso';
          const destDefaultVia = String(
            viaticosConfig?.tipo_boleta_destino || 'interna'
          ).toLowerCase().trim();
          const destRawCheck = String(
            fila._viatico.destino || fila.destino_boleta || ''
          ).trim().toLowerCase();
          const destinosValidos = ['plame', 'interna', 'ambas', 'ambos', 'plame+interna', 'interno', ''];
          if (planillaAlternativaActiva && destRawCheck && !destinosValidos.includes(destRawCheck)) {
            errFila.push('Destino boleta de viático inválido. Use: plame, interna o ambas');
          }
          const destinosVia = planillaAlternativaActiva
            ? expandirDestinosBoleta(destRawCheck, destDefaultVia)
            : ['plame'];
          const obsRecibo = fila._viatico.recibo
            ? `N° recibo: ${fila._viatico.recibo}`
            : null;
          destinosVia.forEach((tb) => {
            conceptosPayload.push({
              plame_configuracion_id: Number(plameId),
              tipo_concepto: tipoConcepto,
              tipo_boleta: tb,
              monto: fila._viatico.monto,
              observaciones: obsRecibo,
            });
          });
        }
      }

      let considerarTasaNocturnaFila = false;
      if (tasaNocturnaActiva) {
        const parsedTasaNocturna = parsearConsiderarTasaNocturnaExcel(fila.considerar_tasa_nocturna);
        if (!parsedTasaNocturna.valido) {
          errFila.push('Considerar tasa nocturna debe ser Si o No (vacío = No)');
        } else {
          considerarTasaNocturnaFila = parsedTasaNocturna.valor;
        }
      }

      if (!errFila.length && emp && fecha && proyectoIdResuelto) {
        const clave = claveUnicaTareo({
          empresa_id: selectedCompany.id,
          empleado_id: emp.id,
          proyecto_id: proyectoIdResuelto,
          fecha,
        });
        if (clavesEnArchivo.has(clave)) {
          errFila.push(`Tareo duplicado en el archivo (mismo empleado, proyecto y fecha). Ya aparece en la fila ${clavesEnArchivo.get(clave)}.`);
        } else {
          clavesEnArchivo.set(clave, nFila);
        }
      }

      if (errFila.length) {
        errores.push({ fila: nFila, nombre: (emp?.nombre_completo ?? dni) || '-', mensaje: errFila.join('; ') });
      } else {
        let horaEntradaReal = normalizarHora(fila.hora_entrada_real) || null;
        let horaSalidaReal = normalizarHora(fila.hora_salida_real) || null;
        if (TIPOS_EVENTO_HORAS_REALES_NULAS.includes(tipo)) {
          horaEntradaReal = null;
          horaSalidaReal = null;
        } else if (esTareoSimple || TIPOS_EVENTO_REAL_IGUAL_TEORICO.includes(tipo)) {
          horaEntradaReal = he;
          horaSalidaReal = hs;
        }
        validas.push({
          fila: nFila,
          empresa_id: selectedCompany.id,
          empleado_id: emp.id,
          proyecto_id: proyectoIdResuelto,
          _sedeNombre: (projects || []).find((p) => String(p.id) === String(proyectoIdResuelto))?.nombre
            || emp?.proyecto
            || fila.sede
            || '',
          fecha,
          tipo_evento: tipo,
          hora_entrada_teorica: he,
          hora_salida_teorica: hs,
          hora_entrada_real: horaEntradaReal,
          hora_salida_real: horaSalidaReal,
          observaciones: String(fila.observaciones ?? '').trim() || null,
          ...(tasaNocturnaActiva ? { considerar_tasa_nocturna: considerarTasaNocturnaFila } : {}),
          conceptos: conceptosPayload.length ? conceptosPayload : undefined
        });
      }
    });

    let nuevas = validas;
    let yaExistentes = [];
    if (validas.length && selectedCompany?.id) {
      const fechas = validas.map((f) => f.fecha).filter(Boolean).sort();
      setConsultandoDuplicados(true);
      try {
        const res = await tareoService.obtenerTareosPorPeriodo(
          fechas[0],
          fechas[fechas.length - 1],
          selectedCompany.id
        );
        const existentes = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
        const mapaBd = mapaClavesTareosExistentes(existentes);
        yaExistentes = validas
          .filter((f) => mapaBd.has(claveUnicaTareo(f)))
          .map((f) => {
            const registrado = mapaBd.get(claveUnicaTareo(f));
            return {
              ...f,
              _registrado: registrado || null,
              _dondeRegistrado: describirTareoRegistrado(registrado || f, projects),
            };
          });
        nuevas = validas.filter((f) => !mapaBd.has(claveUnicaTareo(f)));
      } catch (err) {
        console.warn('No se pudieron consultar tareos existentes para detectar duplicados:', err);
      }
      setConsultandoDuplicados(false);
    }

    setErroresValidacion(errores);
    setFilasYaExistentes(yaExistentes);
    setFilasValidas(nuevas);
    setMostrarValidaciones(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setArchivo(file);
      procesarArchivo(file);
    }
  };

  const descargarPlantilla = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Planillas - Importador Tareos';

    const columnasSimple = [
      ['DNI', 'Documento del empleado (obligatorio)', '8 dígitos, debe existir en el proyecto'],
      ...(esMultiSede ? [['Proyecto', 'Nombre del proyecto (recomendado en multi-proyecto)', 'Debe coincidir con el proyecto del empleado']] : []),
      ['Fecha', 'Fecha del tareo', 'YYYY-MM-DD (ej: 2026-06-01). También acepta DD-MM-YYYY'],
      ['Tipo evento', 'Tipo de registro (obligatorio)', 'Ver lista completa abajo'],
      ['Observaciones', 'Comentarios (opcional)', 'Texto libre'],
      ['Concepto', 'Código PLAME o nombre del concepto (opcional)', 'Ej: 0601, TARDANZAS, ADELANTO'],
      ['Monto concepto', 'Monto del concepto si aplica (opcional)', 'Número mayor a 0. Si pone monto, debe indicar Concepto'],
    ];

    const columnasCompleta = [
      ['DNI', 'Documento del empleado (obligatorio)', '8 dígitos, debe existir en la empresa'],
      ...(esMultiSede ? [['Proyecto', 'Nombre del proyecto (recomendado en multi-proyecto)', 'Debe coincidir con el proyecto del empleado']] : []),
      ['Fecha', 'Fecha del tareo', 'YYYY-MM-DD (ej: 2026-06-01). También acepta DD-MM-YYYY'],
      ['Tipo evento', 'Tipo de registro (obligatorio). Use exactamente el valor de la columna "Valor en Excel"', 'Ver lista completa abajo'],
      ['Hora entrada teórica', 'Hora de entrada programada', 'HH:MM (ej: 08:00)'],
      ['Hora salida teórica', 'Hora de salida programada', 'HH:MM (ej: 17:00)'],
      ['Hora entrada real', 'Hora real de entrada (opcional)', 'HH:MM o vacío'],
      ['Hora salida real', 'Hora real de salida (opcional)', 'HH:MM o vacío'],
      ['Observaciones', 'Comentarios (opcional)', 'Texto libre'],
      ['Concepto', 'Código PLAME o nombre del concepto (opcional)', 'Ej: 0601, TARDANZAS, ADELANTO'],
      ['Monto concepto', 'Monto del concepto si aplica (opcional)', 'Número mayor a 0. Si pone monto, debe indicar Concepto'],
    ];

    const columnasLeyenda = insertarColumnaTasaNocturnaEnLeyenda(
      esTareoSimple ? columnasSimple : columnasCompleta,
      tasaNocturnaActiva
    );

    // Hoja Leyenda
    const wsLeyenda = wb.addWorksheet('Leyenda', { views: [{ state: 'frozen', ySplit: 0 }] });
    const leyendaRows = [
      [esTareoSimple ? 'LEYENDA - IMPORTACIÓN DE TAREOS (MODO SIMPLE)' : 'LEYENDA - IMPORTACIÓN DE TAREOS'],
      [],
      ['Columna', 'Descripción', 'Valores / Formato'],
      ...columnasLeyenda,
      [],
      ['TIPOS DE EVENTO VÁLIDOS (escriba en la columna "Tipo evento" el Valor en Excel o el Código corto):'],
      ['Código', 'Valor en Excel', 'Nombre'],
      ...TAREO_EVENTO_CODIGOS.map((c) => [c.codigo, c.tipo, c.nombre]),
      [],
      ...(esTareoSimple
        ? [
            ['MODO TAREO SIMPLE:'],
            ['Regla', 'Detalle'],
            ['Horas', 'No registre horas en el Excel. El sistema usará el horario del empleado (entrada/salida configurados en su ficha) y asumirá la jornada completa (8 horas).'],
            ['Asistió / feriado laborado / descanso laborado', 'Se registra la jornada del empleado como trabajada.'],
            ['Falta, feriado, descanso, permiso, etc.', 'Se conserva el horario teórico del empleado y las horas reales quedan en 0.'],
            ['Conceptos', 'Puede indicar Concepto y Monto concepto por fila (opcional). Use código PLAME o nombre del concepto.'],
            ['Plantilla calendario', 'También puede descargar la plantilla calendario del mes (columnas = días) y usar los mismos códigos cortos.'],
          ]
        : [
      ['HORAS REALES POR TIPO DE EVENTO:'],
      ['Tipo evento', 'Regla', 'En Excel: Hora entrada real / Hora salida real'],
      ['falta, feriado, descanso, permiso, suspension, vacaciones, descanso_medico, reposo_medico, subsidio_por_enfermedad', 'El sistema conserva el teórico pero registra horas reales en 0.', 'DEJAR VACÍO (obligatorio).'],
      ['feriado_laborado, descanso_laborado', 'Se tratan como asistió puntual: el sistema siempre usa los horarios teóricos como reales.', 'Deje vacío o igual al teórico; se guardarán como teórico.'],
      ['asistio', 'Se usan las horas reales que indique.', 'Opcional; si vacío depende de la configuración del proyecto.'],
          ]),
      [],
      ...(tasaNocturnaActiva
        ? [
            ['TASA NOCTURNA:'],
            ['Columna', 'Regla'],
            ['Considerar tasa nocturna', 'Escriba Si o No por cada fila. Vacío = No. Solo disponible si la empresa tiene habilitada la tasa nocturna.'],
            [],
          ]
        : []),
      ['Nota: El empleado debe estar activo en la fecha indicada según su historial laboral (fecha de ingreso/cese).'],
      ['Nota: No se permiten dos tareos del mismo empleado, en el mismo proyecto y la misma fecha. El sistema detecta repeticiones en el archivo y las que ya están registradas.']
    ];
    leyendaRows.forEach((row, i) => {
      const r = wsLeyenda.getRow(i + 1);
      r.values = [undefined, ...row];
      if (i === 0) r.font = { bold: true, size: 14 };
      if (i === 2 || (Array.isArray(row) && String(row[0] || '').startsWith('TIPOS DE EVENTO')) || row[0] === 'MODO TAREO SIMPLE:' || row[0] === 'HORAS REALES POR TIPO DE EVENTO:' || row[0] === 'Código') {
        r.font = { bold: true };
      }
    });
    wsLeyenda.getColumn(1).width = 22;
    wsLeyenda.getColumn(2).width = 50;
    wsLeyenda.getColumn(3).width = 60;

    // Hoja Tareos (datos)
    const wsTareos = wb.addWorksheet('Tareos', { views: [{ state: 'frozen', ySplit: 1 }] });
    const headers = construirHeadersPlantilla(esTareoSimple, tasaNocturnaActiva, planillaAlternativaActiva, esMultiSede);
    const headerRow = wsTareos.getRow(1);
    headerRow.values = [undefined, ...headers];
    headerRow.font = { bold: true };
    const dniEjemplo = empleadosImportables[0]?.numero_documento || '12345678';
    const sedeEjemplo = empleadosImportables[0]?.proyecto || projects[0]?.nombre || 'Proyecto';
    const fechaEjemplo = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-15`;
    const conSede = (fila) => (esMultiSede ? [fila[0], sedeEjemplo, ...fila.slice(1)] : fila);
    const datosEjemplo = (esTareoSimple
      ? [
          [dniEjemplo, fechaEjemplo, 'asistio', '', ...(tasaNocturnaActiva ? ['No'] : []), '', ''],
          [dniEjemplo, `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-16`, 'falta', 'Justificado', ...(tasaNocturnaActiva ? ['No'] : []), '', ''],
          [dniEjemplo, `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-17`, 'asistio', '', ...(tasaNocturnaActiva ? ['Si'] : []), 'TARDANZAS', '50'],
        ]
      : [
          ['12345678', '2025-01-15', 'asistio', '08:00', '17:00', '08:00', '17:00', '', ...(tasaNocturnaActiva ? ['No'] : []), '', ''],
          ['87654321', '2025-01-15', 'falta', '08:00', '17:00', '', '', 'Justificado', ...(tasaNocturnaActiva ? ['No'] : []), '', ''],
          ['12345678', '2025-01-16', 'asistio', '08:00', '17:00', '08:15', '17:00', '', ...(tasaNocturnaActiva ? ['Si'] : []), 'TARDANZAS', '50']
        ]
    ).map(conSede);
    datosEjemplo.forEach((fila, i) => {
      const r = wsTareos.getRow(i + 2);
      r.values = [undefined, ...fila];
      const fechaCol = headers.indexOf('Fecha') + 1;
      if (fechaCol > 0) {
        r.getCell(fechaCol).numFmt = '@';
      }
    });
    headers.forEach((_, idx) => {
      wsTareos.getColumn(idx + 1).width = Math.max(18, (headers[idx] || '').length + 2);
    });

    // Hoja Empleados permitidos (según mes/año y proyecto/sedes)
    const wsEmpleados = wb.addWorksheet('Empleados permitidos', { views: [{ state: 'frozen', ySplit: 1 }] });
    const mesNombre = MESES[selectedMonth] || '';
    wsEmpleados.getRow(1).values = [undefined, `Empleados que puede incluir en el tareo - ${selectedProject?.nombre || 'Todos los proyectos'} - ${mesNombre} ${selectedYear}`];
    wsEmpleados.getRow(1).font = { bold: true, size: 12 };
    wsEmpleados.getRow(2).values = esMultiSede
      ? [undefined, 'DNI', 'Nombre completo', 'Cargo', 'Proyecto']
      : [undefined, 'DNI', 'Nombre completo', 'Cargo'];
    wsEmpleados.getRow(2).font = { bold: true };
    empleadosImportables.forEach((emp, i) => {
      const r = wsEmpleados.getRow(i + 3);
      r.values = esMultiSede
        ? [undefined, emp.numero_documento || '', emp.nombre_completo || '', emp.cargo || '', emp.proyecto || '']
        : [undefined, emp.numero_documento || '', emp.nombre_completo || '', emp.cargo || ''];
    });
    wsEmpleados.getColumn(1).width = 14;
    wsEmpleados.getColumn(2).width = 35;
    wsEmpleados.getColumn(3).width = 25;
    if (esMultiSede) wsEmpleados.getColumn(4).width = 22;

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = esMultiSede
      ? (esTareoSimple ? 'plantilla_importacion_tareos_todos_proyectos_simple.xlsx' : 'plantilla_importacion_tareos_todos_proyectos.xlsx')
      : (esTareoSimple ? 'plantilla_importacion_tareos_simple.xlsx' : 'plantilla_importacion_tareos.xlsx');
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Plantilla calendario: Tareo (izq.) + Viáticos (der.) en la misma hoja/filas. */
  const descargarPlantillaCalendario = async () => {
    const flags = await resolverFlagsPlantilla();
    const viaActivo = flags.viaActivo;
    const viaCfg = flags.viaCfg;
    const paActiva = flags.paActiva;
    const reciboActivo = flags.numeroRecibo;

    const wb = new ExcelJS.Workbook();
    wb.creator = 'Planillas - Tareo calendario';
    const mesNombre = MESES[selectedMonth] || '';
    const totalDias = diasDelMes(selectedYear, selectedMonth);
    const proyectoNombre = esMultiSede ? 'Todos los proyectos' : (selectedProject?.nombre || 'Proyecto');

    // Columnas fijas: DNI | Nombre [| Proyecto]
    const colsFijas = esMultiSede ? 3 : 2;
    const colSede = esMultiSede ? 3 : 0;
    const colPrimeraDia = colsFijas + 1;

    const subcolsViatico = [];
    if (reciboActivo) subcolsViatico.push('N° recibo');
    subcolsViatico.push('Monto');
    if (paActiva) subcolsViatico.push('Destino');
    const colsPorDiaViatico = Math.max(1, subcolsViatico.length);
    const colInicioVia = colsFijas + totalDias + 1; // Excel 1-based: tras fijas + días 1..N
    const totalColsVia = totalDias * colsPorDiaViatico;

    // Hoja Leyenda
    const wsLeyenda = wb.addWorksheet('Leyenda', { views: [{ state: 'frozen', ySplit: 1 }] });
    const leyendaRows = [
      [`LEYENDA - TAREO CALENDARIO (${mesNombre.toUpperCase()} ${selectedYear})`],
      [],
      ['Cómo usar'],
      ['1. Elija mes/año en el sistema antes de descargar.'],
      ['2. Izquierda (Tareo): complete cada día con código (X, F, D, FL…). Vacío o "-" = sin registro.'],
      ['3. El sistema usará el horario del empleado (modo simple).'],
      ...(esMultiSede
        ? [['   Columna Proyecto: indique el proyecto de cada empleado (ya viene precargada). No la borre.']]
        : []),
      ...(viaActivo
        ? [
            ['4. Derecha (VIATICOS): bajo cada día complete ' + subcolsViatico.join(' | ') + '.'],
            ...(paActiva
              ? [['   Destino: plame | interna | ambas (vacío = destino por defecto de Configuración / Viáticos).']]
              : []),
            [`   Código PLAME de viáticos: ${viaCfg?.codigo_plame || '—'}`],
          ]
        : []),
      [],
      ['Código', 'Evento', 'Valor interno'],
      ...TAREO_EVENTO_CODIGOS.map((c) => [c.codigo, c.nombre, c.tipo]),
      [],
      ['Alias aceptados', 'A o AS = Asistió (igual que X)'],
      [],
      ['Días de la semana', 'L=Lunes M=Martes X=Miércoles J=Jueves V=Viernes S=Sábado D=Domingo'],
      [],
      [`Ámbito: ${proyectoNombre}`, `Empleados: ${empleadosImportables.length}`],
      ...(viaActivo ? [] : [['Nota', 'Viáticos standard no están activos para esta empresa (Configuración).']]),
    ];
    leyendaRows.forEach((row, i) => {
      const r = wsLeyenda.getRow(i + 1);
      r.values = [undefined, ...(Array.isArray(row) ? row : [row])];
      if (i === 0 || i === 2 || (Array.isArray(row) && row[0] === 'Código')) r.font = { bold: true };
    });
    wsLeyenda.getColumn(1).width = 18;
    wsLeyenda.getColumn(2).width = 55;
    wsLeyenda.getColumn(3).width = 28;

    const emps = empleadosImportables.length > 0
      ? empleadosImportables
      : [{ numero_documento: '12345678', nombre_completo: 'EMPLEADO EJEMPLO', proyecto: projects[0]?.nombre || 'Proyecto' }];

    const ws = wb.addWorksheet('Tareo', { views: [{ state: 'frozen', xSplit: colsFijas, ySplit: 3 }] });

    // ——— Fila 1: título tareo | banner VIATICOS ———
    ws.getRow(1).getCell(1).value = `TAREO — ${proyectoNombre} — ${mesNombre.toUpperCase()} ${selectedYear}`;
    ws.getRow(1).getCell(1).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    ws.getRow(1).getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
    try { ws.mergeCells(1, 1, 1, colsFijas + totalDias); } catch (_) { /* ignore */ }

    if (viaActivo) {
      const cellVia = ws.getRow(1).getCell(colInicioVia);
      cellVia.value = 'VIATICOS';
      cellVia.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      cellVia.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB45309' } };
      cellVia.alignment = { horizontal: 'center', vertical: 'middle' };
      try { ws.mergeCells(1, colInicioVia, 1, colInicioVia + totalColsVia - 1); } catch (_) { /* ignore */ }
      for (let c = colInicioVia; c < colInicioVia + totalColsVia; c++) {
        ws.getRow(1).getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB45309' } };
      }
    }

    // ——— Fila 2: letras día (tareo) | números de día (viáticos) ———
    for (let d = 1; d <= totalDias; d++) {
      const cell = ws.getRow(2).getCell(colPrimeraDia - 1 + d);
      cell.value = letraDiaSemana(selectedYear, selectedMonth, d);
      cell.alignment = { horizontal: 'center' };
      cell.font = { bold: true, size: 9, color: { argb: 'FF6B7280' } };
      if (['D', 'S'].includes(letraDiaSemana(selectedYear, selectedMonth, d))) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
      }
    }
    if (viaActivo) {
      for (let d = 1; d <= totalDias; d++) {
        const startCol = colInicioVia + (d - 1) * colsPorDiaViatico;
        const endCol = startCol + colsPorDiaViatico - 1;
        const cell = ws.getRow(2).getCell(startCol);
        cell.value = d;
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD97706' } };
        if (endCol > startCol) {
          try { ws.mergeCells(2, startCol, 2, endCol); } catch (_) { /* ignore */ }
        }
        for (let c = startCol; c <= endCol; c++) {
          ws.getRow(2).getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD97706' } };
          ws.getRow(2).getCell(c).font = { bold: true, color: { argb: 'FFFFFFFF' } };
        }
      }
    }

    // ——— Fila 3: DNI | Nombre [| Proyecto] | días tareo | subcols viático ———
    const headerRow = ws.getRow(3);
    headerRow.getCell(1).value = 'DNI';
    headerRow.getCell(2).value = 'Apellidos y nombres';
    if (esMultiSede) {
      headerRow.getCell(colSede).value = 'Proyecto';
    }
    for (let d = 1; d <= totalDias; d++) {
      const cell = headerRow.getCell(colPrimeraDia - 1 + d);
      cell.value = d;
      cell.alignment = { horizontal: 'center' };
    }
    for (let c = 1; c <= colsFijas + totalDias; c++) {
      const cell = headerRow.getCell(c);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: c === colSede ? 'FF4338CA' : 'FF0F766E' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    }
    if (viaActivo) {
      for (let d = 1; d <= totalDias; d++) {
        const startCol = colInicioVia + (d - 1) * colsPorDiaViatico;
        subcolsViatico.forEach((nombre, i) => {
          const cell = headerRow.getCell(startCol + i);
          cell.value = nombre;
          cell.alignment = { horizontal: 'center', wrapText: true };
          cell.font = { bold: true, size: 8, color: { argb: 'FFFFFFFF' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEA580C' } };
        });
      }
    }
    headerRow.height = 28;

    // ——— Datos empleados (tareo + viáticos misma fila) ———
    emps.forEach((emp, i) => {
      const r = ws.getRow(4 + i);
      r.getCell(1).value = emp.numero_documento || '';
      r.getCell(1).numFmt = '@';
      r.getCell(2).value = emp.nombre_completo || '';
      if (esMultiSede) {
        r.getCell(colSede).value = emp.proyecto || '';
        r.getCell(colSede).alignment = { horizontal: 'center' };
      }
      for (let d = 1; d <= totalDias; d++) {
        const cell = r.getCell(colPrimeraDia - 1 + d);
        cell.value = empleadosImportables.length === 0 && d <= 4 ? ['X', 'X', 'D', 'F'][d - 1] : '';
        cell.alignment = { horizontal: 'center' };
        if (['D', 'S'].includes(letraDiaSemana(selectedYear, selectedMonth, d))) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
        }
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        };
      }
      if (viaActivo) {
        for (let d = 1; d <= totalDias; d++) {
          const startCol = colInicioVia + (d - 1) * colsPorDiaViatico;
          subcolsViatico.forEach((nombre, idx) => {
            const cell = r.getCell(startCol + idx);
            cell.value = '';
            cell.alignment = { horizontal: 'center' };
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFFCD34D' } },
              left: { style: 'thin', color: { argb: 'FFFCD34D' } },
              bottom: { style: 'thin', color: { argb: 'FFFCD34D' } },
              right: { style: 'thin', color: { argb: 'FFFCD34D' } },
            };
            if (nombre === 'Monto') cell.numFmt = '0.00';
            if (empleadosImportables.length === 0 && d === 1) {
              if (nombre === 'Destino') cell.value = 'plame';
              if (nombre === 'Monto') cell.value = 50;
              if (nombre === 'N° recibo') cell.value = 'R-001';
            }
          });
        }
      }
    });

    ws.getColumn(1).width = 12;
    ws.getColumn(2).width = 32;
    if (esMultiSede) ws.getColumn(colSede).width = 18;
    for (let d = 1; d <= totalDias; d++) ws.getColumn(colPrimeraDia - 1 + d).width = 3.6;
    if (viaActivo) {
      for (let c = colInicioVia; c < colInicioVia + totalColsVia; c++) {
        ws.getColumn(c).width = 9;
      }
    }

    const pieRow = 5 + Math.max(emps.length, 1);
    ws.getRow(pieRow).getCell(1).value = 'Leyenda rápida:';
    ws.getRow(pieRow).font = { bold: true, size: 9 };
    ws.getRow(pieRow + 1).getCell(1).value = TAREO_EVENTO_CODIGOS.map((c) => `${c.codigo}=${c.nombre}`).join('  |  ');
    ws.getRow(pieRow + 1).font = { size: 8, color: { argb: 'FF4B5563' } };
    if (esMultiSede) {
      ws.getRow(pieRow + (viaActivo ? 3 : 2)).getCell(1).value =
        'Multi-proyecto: la columna Proyecto determina a qué proyecto va cada fila. Debe coincidir con el nombre del proyecto.';
      ws.getRow(pieRow + (viaActivo ? 3 : 2)).font = { size: 9, italic: true, color: { argb: 'FF3730A3' } };
    }
    if (viaActivo) {
      ws.getRow(pieRow + 2).getCell(1).value = paActiva
        ? 'Viáticos (derecha): Destino = plame | interna | ambas. Deje vacío si no aplica.'
        : 'Viáticos (derecha): complete Monto' + (reciboActivo ? ' y N° recibo' : '') + ' por día. Deje vacío si no aplica.';
      ws.getRow(pieRow + 2).font = { size: 9, italic: true, color: { argb: 'FF92400E' } };
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const mesSlug = String(selectedMonth + 1).padStart(2, '0');
    const slugAmbito = esMultiSede ? 'todos_proyectos' : proyectoNombre.replace(/\s+/g, '_').slice(0, 24);
    a.download = `tareo_calendario_${slugAmbito}_${selectedYear}_${mesSlug}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const descargarPlantillaRegularizar = async () => {
    const flags = await resolverFlagsPlantilla();
    const paActiva = flags.paActiva;

    const wb = new ExcelJS.Workbook();
    wb.creator = 'Planillas - Regularizar conceptos';

    const wsLeyenda = wb.addWorksheet('Leyenda', { views: [{ state: 'frozen', ySplit: 0 }] });
    const leyendaRows = [
      ['LEYENDA - REGULARIZAR CONCEPTOS EN TAREOS EXISTENTES'],
      [],
      ['Este archivo sirve para AÑADIR conceptos (adelanto, ingreso o descuento) a tareos ya creados.'],
      ['No crea tareos nuevos. Solo busca el tareo por DNI + Fecha + Proyecto y le agrega el concepto indicado.'],
      [],
      ['Columna', 'Descripción', 'Valores / Formato'],
      ['DNI', 'Documento del empleado (obligatorio)', 'Debe existir en el proyecto y tener un tareo en la fecha indicada'],
      ['Fecha', 'Fecha del tareo existente', 'YYYY-MM-DD (ej: 2025-01-15)'],
      ['Concepto', 'Código PLAME o nombre del concepto (obligatorio)', 'Use código o nombre de la lista "Conceptos disponibles" más abajo'],
      ['Monto concepto', 'Monto del concepto (obligatorio)', 'Número mayor a 0'],
      ...(paActiva
        ? [['Destino boleta', 'A qué boleta va el concepto (planilla alternativa)', 'plame | interna | ambas (vacío = plame)']]
        : []),
      ['Observaciones', 'Comentarios opcionales', 'Texto libre'],
      [],
      ['Personal al que aplica:'],
      ['Solo puede regularizar conceptos para empleados del proyecto seleccionado que estén activos en el mes/año elegido.'],
      ['La hoja "Empleados permitidos" contiene la lista de DNI y nombres que puede usar.'],
      ['El tareo debe existir previamente (mismo empleado, misma fecha, mismo proyecto).'],
      [],
      ['CONCEPTOS DISPONIBLES (use Código o Nombre en la columna Concepto):'],
    ];
    leyendaRows.forEach((row, i) => {
      const r = wsLeyenda.getRow(i + 1);
      r.values = [undefined, ...(Array.isArray(row) ? row : [row])];
      if (i === 0) r.font = { bold: true, size: 14 };
      if (i === 5) r.font = { bold: true };
      if (i === leyendaRows.length - 1) r.font = { bold: true };
    });
    let rowLeyenda = leyendaRows.length + 1;
    wsLeyenda.getRow(rowLeyenda).values = [undefined, 'Código', 'Nombre', 'Tipo'];
    wsLeyenda.getRow(rowLeyenda).font = { bold: true };
    rowLeyenda += 1;
    (plameConceptos || []).forEach((c) => {
      const r = wsLeyenda.getRow(rowLeyenda);
      r.values = [undefined, c.codigo_plame || c.codigo || '', c.nombre || '', tipoConceptoParaTareo(c) || ''];
      rowLeyenda += 1;
    });
    wsLeyenda.getColumn(1).width = 22;
    wsLeyenda.getColumn(2).width = 55;
    wsLeyenda.getColumn(3).width = 60;

    const wsReg = wb.addWorksheet('Regularizar conceptos', { views: [{ state: 'frozen', ySplit: 1 }] });
    const headersReg = paActiva
      ? ['DNI', 'Fecha', 'Concepto', 'Monto concepto', 'Destino boleta', 'Observaciones']
      : ['DNI', 'Fecha', 'Concepto', 'Monto concepto', 'Observaciones'];
    wsReg.getRow(1).values = [undefined, ...headersReg];
    wsReg.getRow(1).font = { bold: true };
    const ejemploReg = paActiva
      ? [
          ['12345678', '2025-01-15', 'TARDANZAS', '50', 'plame', ''],
          ['87654321', '2025-01-16', '0601', '100', 'ambas', 'Adelanto'],
          ['11223344', '2025-01-17', '0705', '30', 'interna', 'Descuento solo boleta interna'],
        ]
      : [
      ['12345678', '2025-01-15', 'TARDANZAS', '50', ''],
      ['87654321', '2025-01-16', '0601', '100', 'Adelanto'],
    ];
    ejemploReg.forEach((fila, i) => {
      const r = wsReg.getRow(i + 2);
      r.values = [undefined, ...fila];
    });
    headersReg.forEach((h, idx) => {
      wsReg.getColumn(idx + 1).width = Math.max(16, (h || '').length + 2);
    });

    const wsEmpleadosReg = wb.addWorksheet('Empleados permitidos', { views: [{ state: 'frozen', ySplit: 1 }] });
    const mesNombre = MESES[selectedMonth] || '';
    wsEmpleadosReg.getRow(1).values = [undefined, `Personal al que aplica - ${selectedProject?.nombre || 'Proyecto'} - ${mesNombre} ${selectedYear}`];
    wsEmpleadosReg.getRow(1).font = { bold: true, size: 12 };
    wsEmpleadosReg.getRow(2).values = [undefined, 'DNI', 'Nombre completo', 'Cargo'];
    wsEmpleadosReg.getRow(2).font = { bold: true };
    empleadosImportables.forEach((emp, i) => {
      const r = wsEmpleadosReg.getRow(i + 3);
      r.values = [undefined, emp.numero_documento || '', emp.nombre_completo || '', emp.cargo || ''];
    });
    wsEmpleadosReg.getColumn(1).width = 14;
    wsEmpleadosReg.getColumn(2).width = 35;
    wsEmpleadosReg.getColumn(3).width = 25;

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_regularizar_conceptos.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  const procesarArchivoRegularizar = async (file) => {
    setProcesandoRegularizar(true);
    setDatosRegularizar([]);
    setErroresRegularizar([]);
    setFilasValidasRegularizar([]);
    setMostrarValidacionesRegularizar(false);
    setResultadoRegularizar(null);
    const getVal = (obj, keys) => {
      for (const k of keys) {
        const v = normalizarValorCeldaExcel(obj[k]);
        if (v !== undefined && v !== null && v !== '') return v;
      }
      return '';
    };
    try {
      // Refrescar flag PA por si cambió en Configuración
      const flags = await resolverFlagsPlantilla();

      const buffer = await file.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buffer);
      const ws = wb.worksheets.find(s => (s.name || '').toLowerCase().includes('regularizar')) || wb.worksheets[0];
      if (!ws) {
        setErroresRegularizar([{ fila: 0, mensaje: 'No se encontró la hoja "Regularizar conceptos" en el archivo.' }]);
        setMostrarValidacionesRegularizar(true);
        setProcesandoRegularizar(false);
        return;
      }
      const headerRow = ws.getRow(1);
      const headers = [];
      headerRow.eachCell((cell, colNumber) => { headers[colNumber - 1] = String(normalizarValorCeldaExcel(cell.value) ?? '').trim(); });
      const rows = [];
      for (let rowNumber = 2; rowNumber <= (ws.rowCount || 2); rowNumber++) {
        const row = ws.getRow(rowNumber);
        const obj = {};
        row.eachCell((cell, colNumber) => { obj[headers[colNumber - 1]] = normalizarValorCeldaExcel(cell.value); });
        const dni = getVal(obj, ['DNI', 'dni']);
        const concepto = getVal(obj, ['Concepto', 'concepto']);
        const monto = getVal(obj, ['Monto concepto', 'monto_concepto', 'Monto']);
        if (dni === '' && concepto === '' && (monto === '' || monto === undefined)) continue;
        rows.push({
          dni: String(dni).trim(),
          fecha: getVal(obj, ['Fecha', 'fecha']),
          concepto: String(concepto).trim(),
          monto_concepto: monto,
          destino_boleta: String(getVal(obj, ['Destino boleta', 'destino_boleta', 'Destino', 'destino'])).trim(),
          observaciones: String(getVal(obj, ['Observaciones', 'observaciones'])).trim(),
        });
      }
      setDatosRegularizar(rows);
      validarDatosRegularizar(rows, flags.paActiva);
    } catch (err) {
      setErroresRegularizar([{ fila: 0, mensaje: 'Error al leer el Excel: ' + (err.message || 'archivo inválido') }]);
      setMostrarValidacionesRegularizar(true);
    }
    setProcesandoRegularizar(false);
  };

  const validarDatosRegularizar = (filas, paActivaOverride = null) => {
    const paActiva = paActivaOverride != null ? paActivaOverride : planillaAlternativaActiva;
    const errores = [];
    const validas = [];
    const destinosValidos = ['plame', 'interna', 'ambas', 'ambos', 'plame+interna', 'interno', ''];
    filas.forEach((fila, idx) => {
      const nFila = idx + 2;
      const errFila = [];
      const dni = String(fila.dni ?? '').trim();
      if (!dni) errFila.push('DNI es obligatorio');
      const emp = empleadoPorDni(dni);
      if (dni && !emp) errFila.push('No se encontró empleado con este DNI en el proyecto/empresa');
      const fecha = normalizarFecha(fila.fecha);
      if (!fecha) errFila.push('Fecha inválida o vacía (use YYYY-MM-DD)');
      const conceptoVal = String(fila.concepto ?? '').trim();
      if (!conceptoVal) errFila.push('Concepto es obligatorio');
      const res = resolverConcepto(conceptoVal || fila.concepto);
      if (conceptoVal && !res) errFila.push('Concepto no reconocido (use código PLAME o nombre del concepto)');
      const monto = parseFloat(fila.monto_concepto);
      if (isNaN(monto) || monto <= 0) errFila.push('Monto concepto debe ser un número mayor a 0');

      const destRaw = String(fila.destino_boleta || '').trim().toLowerCase();
      if (paActiva && destRaw && !destinosValidos.includes(destRaw)) {
        errFila.push('Destino boleta inválido. Use: plame, interna o ambas');
      }

      if (errFila.length) {
        errores.push({ fila: nFila, nombre: (emp?.nombre_completo ?? dni) || '-', mensaje: errFila.join('; ') });
      } else {
        const destinos = paActiva
          ? expandirDestinosBoleta(destRaw, 'plame')
          : ['plame'];
        const conceptosPayload = destinos.map((tb) => ({
          plame_configuracion_id: res.id,
          tipo_concepto: res.tipo_concepto,
          tipo_boleta: tb,
          monto,
          observaciones: (fila.observaciones || null),
        }));
        validas.push({
          fila: nFila,
          empresa_id: selectedCompany.id,
          empleado_id: emp.id,
          // En modo global el backend resolverá el tareo por empresa+empleado+fecha.
          // En modo por proyecto se envía el proyecto seleccionado.
          proyecto_id: selectedProject?.id || null,
          fecha,
          conceptos: conceptosPayload,
        });
      }
    });
    setErroresRegularizar(errores);
    setFilasValidasRegularizar(validas);
    setMostrarValidacionesRegularizar(true);
  };

  const ejecutarRegularizacion = async () => {
    if (filasValidasRegularizar.length === 0) return;
    setAplicandoRegularizar(true);
    setResultadoRegularizar(null);
    try {
      const res = await tareoService.regularizarConceptos(filasValidasRegularizar);
      if (res.success) {
        setResultadoRegularizar({ ok: true, data: res.data, message: res.message });
        if (onImportSuccess) onImportSuccess(res);
      } else {
        setResultadoRegularizar({ ok: false, message: res.message || 'Error al regularizar', data: res.data });
      }
    } catch (err) {
      const data = err.response?.data;
      setResultadoRegularizar({ ok: false, message: data?.message || err.message || 'Error al regularizar' });
    }
    setAplicandoRegularizar(false);
  };

  const handleFileSelectRegularizar = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setArchivoRegularizar(file);
      procesarArchivoRegularizar(file);
    }
  };

  const ejecutarImportacion = async () => {
    if (filasValidas.length === 0) return;
    setImportando(true);
    const totalFilas = filasValidas.length;
    const esperaBackground = totalFilas >= 500;

    // Mostrar barra de inmediato (no esperar la respuesta del POST)
    if (esperaBackground) {
      setResultado({
        ok: true,
        background: true,
        importacionId: null,
        message: `Enviando ${totalFilas} tareos al servidor…`,
        progreso: {
          estado: 'enviando',
          total: totalFilas,
          procesados: 0,
          exitosos: 0,
          fallidos: 0,
          progreso: 0,
        },
      });
    } else {
    setResultado(null);
    }

    try {
      const payload = {
        tareos: prepararTareosImportacion(filasValidas, tasaNocturnaActiva),
        crear_autorizaciones_aprobadas: puedeAprobarPermisos
      };
      const res = await tareoService.crearTareoMasivo(payload);

      // Importación en segundo plano (>= 500)
      if (res.success && (res.background || res.data?.importacion_id)) {
        const importacionId = res.data?.importacion_id;
        setResultado({
          ok: true,
          background: true,
          importacionId,
          message: res.message || 'Importación en segundo plano iniciada.',
          progreso: {
            estado: 'pendiente',
            total: res.data?.total || totalFilas,
            procesados: 0,
            exitosos: 0,
            fallidos: 0,
            progreso: 0,
          },
        });

        if (importacionId) {
          const poll = async () => {
            for (let i = 0; i < 1800; i++) { // ~30 min máx (1s * 1800)
              await new Promise((r) => setTimeout(r, 1000));
              const st = await tareoService.obtenerEstadoImportacionMasiva(importacionId);
              if (!st.success || !st.data) continue;
              const d = st.data;
              setResultado((prev) => ({
                ...(prev || {}),
                ok: d.estado !== 'error',
                background: true,
                importacionId,
                message: d.mensaje || prev?.message,
                progreso: d,
              }));
              if (d.estado === 'completado' || d.estado === 'error') {
                if (d.estado === 'completado' && onImportSuccess) {
                  onImportSuccess({ success: true, background: true, data: d });
                }
                break;
              }
            }
          };
          poll().finally(() => setImportando(false));
          return;
        }
      }

      if (res.success) {
        setResultado({ ok: true, data: res.data, message: res.message });
        if (onImportSuccess) onImportSuccess(res);
      } else {
        const erroresActividad = res.errors?.errores_actividad || res.errores_actividad;
        const erroresTipoEvento = res.errors?.errores_tipo_evento || res.errores_tipo_evento;
        const duplicados = res.errors?.duplicados || res.duplicados;
        const msg = res.message || 'Error al importar';
        setResultado({
          ok: false,
          message: msg,
          errores_actividad: erroresActividad,
          errores_tipo_evento: erroresTipoEvento,
          duplicados,
        });
      }
    } catch (err) {
      const data = err.response?.data;
      const erroresActividad = data?.errores_actividad;
      const erroresTipoEvento = data?.errores_tipo_evento;
      const duplicados = data?.duplicados;
      setResultado({
        ok: false,
        message: data?.message || err.message || 'Error al importar',
        errores_actividad: erroresActividad,
        errores_tipo_evento: erroresTipoEvento,
        duplicados,
      });
    }
    setImportando(false);
  };

  const hayErroresActividad = resultado?.errores_actividad?.length > 0;
  const hayErroresTipoEvento = resultado?.errores_tipo_evento?.length > 0;
  const hayDuplicadosResultado = resultado?.duplicados?.length > 0;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full my-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Importar Tareos desde Excel</h2>
              <p className="text-sm text-gray-600 mt-1">
                {soloRegularizarGlobal
                  ? <>Ámbito: <strong>Todo el personal activo</strong> de la empresa. Los empleados deben estar activos en la fecha del tareo (historial laboral).</>
                  : esMultiSede
                    ? <>Ámbito: <strong>Todos los proyectos</strong>. El proyecto se toma de la columna <strong>Proyecto</strong> o del asignado al empleado.</>
                  : <>Proyecto: <strong>{selectedProject?.nombre}</strong>. Los empleados deben estar activos en la fecha del tareo (historial laboral).</>}
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {!soloRegularizarGlobal && (
            <div className="flex border-b border-gray-200 mb-4">
              <button
                type="button"
                onClick={() => {
                  setModoImportacion('importar');
                  setResultado(null);
                  setResultadoRegularizar(null);
                  setArchivoRegularizar(null);
                  setMostrarValidacionesRegularizar(false);
                  setDatosRegularizar([]);
                  setErroresRegularizar([]);
                  setFilasValidasRegularizar([]);
                }}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${modoImportacion === 'importar' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Importar tareos
              </button>
              <button
                type="button"
                onClick={() => {
                  setModoImportacion('regularizar');
                  setResultado(null);
                  setResultadoRegularizar(null);
                  setArchivo(null);
                  setMostrarValidaciones(false);
                  setDatosExcel([]);
                  setErroresValidacion([]);
                  setFilasValidas([]);
                }}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${modoImportacion === 'regularizar' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                Regularizar conceptos
              </button>
            </div>
          )}

          {modoImportacion === 'regularizar' && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Regularizar conceptos:</strong> Añade conceptos (adelanto, ingreso o descuento) a <strong>tareos ya existentes</strong>. No crea tareos nuevos. El empleado debe tener un registro de tareo en la fecha indicada{soloRegularizarGlobal ? ' en su proyecto asignado' : ' para el mismo proyecto'}. Descargue la plantilla con leyenda y la lista de personal al que aplica.
                {planillaAlternativaActiva && (
                  <> Con <strong>planilla alternativa</strong>, use la columna <strong>Destino boleta</strong>: plame, interna o ambas.</>
                )}
              </p>
            </div>
          )}

          {modoImportacion === 'importar' && (
            <div className={`mb-4 p-4 rounded-lg border ${esTareoSimple ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-sm ${esTareoSimple ? 'text-blue-900' : 'text-amber-800'}`}>
                {esTareoSimple ? (
                  <>
                    <strong>Tareo simple activo:</strong> puede importar la plantilla por filas (DNI + Fecha + Tipo)
                    o la <strong>plantilla calendario</strong> (empleados × días del mes con códigos de 1–2 letras: X, F, D, FL…).
                    Elija el mes/año arriba: el calendario ya sale con esos días y la letra del día de la semana.
                    {viaticosActivo && (
                      <> Con <strong>viáticos activos</strong>, a la <strong>derecha</strong> del calendario aparece la franja <strong>VIATICOS</strong>:
                      por cada día,
                      {numeroReciboActivo && <> <strong>N° recibo</strong>,</>}
                      {' '}<strong>Monto</strong>
                      {planillaAlternativaActiva && <> y <strong>Destino</strong> (plame / interna / ambas)</>}.</>
                    )}
                    {planillaAlternativaActiva && !viaticosActivo && (
                      <> Con planilla alternativa, en la plantilla por filas use <strong>Destino boleta</strong>: plame, interna o ambas.</>
                    )}
                    {' '}El sistema toma el horario del empleado (sin columnas de horas).
                  </>
                ) : (
                  <>
                    <strong>Importante:</strong> El sistema validará que cada empleado haya estado activo en la fecha indicada según su historial laboral. La plantilla incluye una hoja <strong>Leyenda</strong> con la descripción de cada columna{tasaNocturnaActiva ? ', incluida <strong>Considerar tasa nocturna</strong> (Si/No por fila)' : ''}. Puede agregar un <strong>Concepto</strong> y su <strong>Monto</strong> por fila (opcional).
                  </>
                )}
              </p>
            </div>
          )}

          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Mes y año del tareo</h3>
            <p className="text-xs text-gray-500 mb-3">
              Elija el mes y año para ver los empleados que puede incluir según el proyecto y su historial laboral. La plantilla descargada incluirá esta lista en la hoja &quot;Empleados permitidos&quot;.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Mes</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                >
                  {MESES.map((nombre, i) => (
                    <option key={i} value={i}>{nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Año</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                >
                  {Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {modoImportacion === 'regularizar' ? 'Personal al que aplica' : 'Empleados que puede incluir en el tareo'} ({empleadosImportables.length})
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              {modoImportacion === 'regularizar'
                ? (soloRegularizarGlobal
                    ? 'Solo puede regularizar conceptos para personal activo de la empresa (el tareo debe existir en la fecha indicada).'
                    : 'Solo puede regularizar conceptos para estos empleados del proyecto (el tareo debe existir en la fecha indicada).')
                : esMultiSede
                  ? <>Todos los proyectos ({projects.length || '—'}), personal activo en {MESES[selectedMonth]} {selectedYear}. Incluya columna <strong>Proyecto</strong> o se usará el del empleado.</>
                  : <>Proyecto <strong>{selectedProject?.nombre}</strong>, activos en {MESES[selectedMonth]} {selectedYear} según historial laboral.</>}
            </p>
            {empleadosImportables.length === 0 ? (
              <p className="text-sm text-amber-700">No hay empleados activos en el mes/año seleccionado.</p>
            ) : (
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">DNI</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Nombre completo</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Cargo</th>
                      {esMultiSede && (
                        <th className="px-3 py-2 text-left font-medium text-indigo-700">Proyecto</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {empleadosImportables.map((emp) => (
                      <tr key={emp.id} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 text-gray-800">{emp.numero_documento || '—'}</td>
                        <td className="px-3 py-1.5 text-gray-800">{emp.nombre_completo || '—'}</td>
                        <td className="px-3 py-1.5 text-gray-600">{emp.cargo || '—'}</td>
                        {esMultiSede && (
                          <td className="px-3 py-1.5 text-indigo-800">{emp.proyecto || '—'}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {modoImportacion === 'importar' && (
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              <h3 className="text-sm font-medium text-gray-700">Archivo Excel</h3>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <button type="button" onClick={descargarPlantilla} className="text-sm text-teal-600 hover:text-teal-800 font-medium">
                  {esTareoSimple ? 'Plantilla por filas (con leyenda)' : 'Descargar plantilla (con leyenda)'}
              </button>
                {esTareoSimple && (
                  <button type="button" onClick={descargarPlantillaCalendario} className="text-sm text-amber-700 hover:text-amber-900 font-medium">
                    {viaticosActivo
                      ? 'Plantilla calendario (Tareo | Viáticos)'
                      : 'Plantilla calendario del mes (códigos X/F/D…)'}
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {esTareoSimple
                ? `Mes seleccionado: ${MESES[selectedMonth]} ${selectedYear} (${diasDelMes(selectedYear, selectedMonth)} días). Calendario: izquierda Tareo (códigos X/F/D…)${viaticosActivo ? `; derecha VIATICOS por día (${[numeroReciboActivo ? 'N° recibo' : null, 'Monto', planillaAlternativaActiva ? 'Destino plame/interna/ambas' : null].filter(Boolean).join(', ')})` : ''}.${planillaAlternativaActiva ? ' En plantilla por filas también: Destino boleta.' : ''}`
                : 'Hoja "Tareos": DNI, Fecha, Tipo evento, Horas teóricas/reales, Observaciones, Concepto (código o nombre), Monto concepto (opcional).'}
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
              {!archivo ? (
                <>
                  <p className="text-gray-600 mb-3">Seleccione un archivo .xlsx o .xls</p>
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700">
                    Seleccionar archivo
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-700 font-medium">{archivo.name}</p>
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-2 text-sm text-teal-600 hover:underline">Cambiar archivo</button>
                </>
              )}
            </div>
            {procesandoArchivo && (
              <div className="mt-3 flex items-center text-amber-700 text-sm">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent mr-2" />
                Procesando archivo...
              </div>
            )}
          </div>
          )}

          {modoImportacion === 'regularizar' && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Archivo Excel - Regularizar conceptos</h3>
              <button type="button" onClick={descargarPlantillaRegularizar} className="text-sm text-teal-600 hover:text-teal-800 font-medium">
                Descargar plantilla (con leyenda y personal)
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Hoja &quot;Regularizar conceptos&quot;: DNI, Fecha, Concepto (código o nombre), Monto concepto
              {planillaAlternativaActiva ? ', Destino boleta (plame / interna / ambas)' : ''}
              , Observaciones. El tareo debe existir para ese empleado y fecha.
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input ref={fileInputRefRegularizar} type="file" accept=".xlsx,.xls" onChange={handleFileSelectRegularizar} className="hidden" />
              {!archivoRegularizar ? (
                <>
                  <p className="text-gray-600 mb-3">Seleccione un archivo .xlsx o .xls</p>
                  <button type="button" onClick={() => fileInputRefRegularizar.current?.click()} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700">
                    Seleccionar archivo
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-700 font-medium">{archivoRegularizar.name}</p>
                  <button type="button" onClick={() => fileInputRefRegularizar.current?.click()} className="mt-2 text-sm text-teal-600 hover:underline">Cambiar archivo</button>
                </>
              )}
            </div>
            {procesandoRegularizar && (
              <div className="mt-3 flex items-center text-amber-700 text-sm">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent mr-2" />
                Procesando archivo...
              </div>
            )}
          </div>
          )}

          {modoImportacion === 'importar' && mostrarValidaciones && (
            <>
              <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                <div className="bg-gray-50 rounded p-3"><span className="text-lg font-bold text-gray-700">{datosExcel.length}</span><p className="text-xs text-gray-600">Total filas</p></div>
                <div className="bg-green-50 rounded p-3"><span className="text-lg font-bold text-green-700">{filasValidas.length}</span><p className="text-xs text-green-700">Nuevas</p></div>
                <div className="bg-amber-50 rounded p-3"><span className="text-lg font-bold text-amber-700">{filasYaExistentes.length}</span><p className="text-xs text-amber-700">Ya existen</p></div>
                <div className="bg-red-50 rounded p-3"><span className="text-lg font-bold text-red-700">{erroresValidacion.length}</span><p className="text-xs text-red-700">Con error</p></div>
              </div>

              {consultandoDuplicados && (
                <div className="mb-4 flex items-center text-amber-800 text-sm">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent mr-2" />
                  Revisando si ya hay tareos registrados para esas fechas...
                </div>
              )}

              {erroresValidacion.length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg max-h-48 overflow-y-auto">
                  <h4 className="font-medium text-red-900 mb-2">Errores de validación (no se puede importar hasta corregirlos)</h4>
                  <ul className="text-sm text-red-800 space-y-1">
                    {erroresValidacion.map((e, i) => (
                      <li key={i}>Fila {e.fila}: {e.nombre} — {e.mensaje}</li>
                    ))}
                  </ul>
                </div>
              )}

              {filasYaExistentes.length > 0 && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg max-h-48 overflow-y-auto">
                  <h4 className="font-medium text-amber-900 mb-2">
                    {filasYaExistentes.length} tareo(s) ya registrados (mismo empleado, proyecto y fecha). Se omitirán al importar.
                  </h4>
                  <ul className="text-sm text-amber-800 space-y-1">
                    {filasYaExistentes.slice(0, 40).map((f, i) => {
                      const emp = employees.find((e) => String(e.id) === String(f.empleado_id));
                      const dni = emp?.numero_documento || '';
                      const nombre = emp?.nombre_completo || dni || f.empleado_id;
                      return (
                        <li key={i}>
                          Fila {f.fila}: {nombre}{dni && nombre !== dni ? ` (${dni})` : ''} — {f.fecha}
                          {' — '}{f._dondeRegistrado || describirTareoRegistrado(f._registrado || f, projects)}
                        </li>
                      );
                    })}
                    {filasYaExistentes.length > 40 && (
                      <li>… y {filasYaExistentes.length - 40} más</li>
                    )}
                  </ul>
                </div>
              )}

              {filasValidas.length > 0 && erroresValidacion.length === 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    {filasYaExistentes.length > 0
                      ? `Se importarán ${filasValidas.length} tareo(s) nuevos. Los ${filasYaExistentes.length} que ya existen se omiten.`
                      : 'Todas las filas son válidas. Puede proceder a importar.'}
                  </p>
                </div>
              )}

              {filasValidas.length === 0 && erroresValidacion.length === 0 && filasYaExistentes.length > 0 && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-900">Todos los tareos del archivo ya están registrados. No hay nada nuevo que importar.</p>
                </div>
              )}

              {filasValidas.length > 0 && (
                <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Vista previa de importación ({filasValidas.length} registros)</h4>
                  <p className="text-xs text-gray-500 mb-3">Revise los datos antes de importar. Así puede detectar errores antes de enviar.</p>
                  <div className="max-h-64 overflow-auto border border-gray-200 rounded">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">#</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">DNI</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Empleado</th>
                          {esMultiSede && (
                            <th className="px-2 py-1.5 text-left font-medium text-indigo-700">Proyecto</th>
                          )}
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Fecha</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Tipo</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Entrada T.</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Salida T.</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Entrada R.</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Salida R.</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Obs.</th>
                          {tasaNocturnaActiva && (
                            <th className="px-2 py-1.5 text-left font-medium text-indigo-700">T. Noct.</th>
                          )}
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Conceptos</th>
                          <th className="px-2 py-1.5 text-left font-medium text-amber-800">Destino</th>
                          <th className="px-2 py-1.5 text-left font-medium text-amber-800">N° recibo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filasValidas.map((fila, idx) => {
                          const emp = employees.find(e => e.id === fila.empleado_id);
                          const conceptos = fila.conceptos || [];
                          const conceptoStr = conceptos.length
                            ? conceptos.map((c) => {
                                const plame = (plameConceptos || []).find((p) => Number(p.id) === Number(c.plame_configuracion_id));
                                const label = plame?.codigo_plame || plame?.codigo || c.plame_configuracion_id || '';
                                return `${label}: ${c.monto ?? ''}`;
                              }).join('; ')
                            : '—';
                          const destinosStr = conceptos.length
                            ? [...new Set(conceptos.map((c) => c.tipo_boleta || 'plame'))].join(', ')
                            : '—';
                          const recibosStr = conceptos.length
                            ? [...new Set(
                                conceptos
                                  .map((c) => {
                                    const m = String(c.observaciones || '').match(/N[°º]?\s*recibo:\s*(.+)/i);
                                    return m ? m[1].trim() : '';
                                  })
                                  .filter(Boolean)
                              )].join(', ') || '—'
                            : '—';
                          return (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-2 py-1 text-gray-600">{fila.fila}</td>
                              <td className="px-2 py-1 text-gray-800">{emp?.numero_documento ?? '—'}</td>
                              <td className="px-2 py-1 text-gray-800 max-w-[120px] truncate" title={emp?.nombre_completo}>{emp?.nombre_completo ?? '—'}</td>
                              {esMultiSede && (
                                <td className="px-2 py-1 text-indigo-800 font-medium truncate max-w-[90px]" title={fila._sedeNombre || ''}>
                                  {fila._sedeNombre || '—'}
                                </td>
                              )}
                              <td className="px-2 py-1 text-gray-800">{fila.fecha}</td>
                              <td className="px-2 py-1 text-gray-800">{fila.tipo_evento}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.hora_entrada_teorica || '—'}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.hora_salida_teorica || '—'}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.hora_entrada_real || '—'}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.hora_salida_real || '—'}</td>
                              <td className="px-2 py-1 text-gray-600 max-w-[80px] truncate" title={fila.observaciones || ''}>{fila.observaciones || '—'}</td>
                              {tasaNocturnaActiva && (
                                <td className="px-2 py-1 text-indigo-700 font-medium">
                                  {fila.considerar_tasa_nocturna ? 'Si' : 'No'}
                                </td>
                              )}
                              <td className="px-2 py-1 text-gray-600 max-w-[120px] truncate" title={conceptoStr}>{conceptoStr}</td>
                              <td className="px-2 py-1 text-amber-800 font-medium" title={destinosStr}>{destinosStr}</td>
                              <td className="px-2 py-1 text-amber-800 max-w-[90px] truncate" title={recibosStr}>{recibosStr}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {filasValidas.length > 0 && (
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={ejecutarImportacion}
                    disabled={importando || consultandoDuplicados || erroresValidacion.length > 0}
                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed w-fit"
                  >
                    {importando
                      ? (resultado?.progreso?.estado === 'enviando'
                          ? 'Enviando al servidor...'
                          : 'Importando...')
                      : `Importar ${filasValidas.length} tareo(s)`}
                  </button>

                  {resultado?.background && resultado?.progreso && (
                    <div className={`p-4 rounded-lg ${resultado.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <p className={`text-sm ${resultado.ok ? 'text-green-800' : 'text-red-800'}`}>{resultado.message}</p>
                      <div className="mt-3">
                        <div className={`flex justify-between text-xs mb-1 ${resultado.ok ? 'text-green-900' : 'text-red-900'}`}>
                          <span>
                            Estado: <strong>{resultado.progreso.estado}</strong>
                            {resultado.importacionId ? ` (#${resultado.importacionId})` : ''}
                          </span>
                          <span>
                            {resultado.progreso.procesados || 0}/{resultado.progreso.total || 0}
                            {' '}({resultado.progreso.progreso || 0}%)
                          </span>
                        </div>
                        <div className={`w-full rounded-full h-3 overflow-hidden ${resultado.ok ? 'bg-green-100' : 'bg-red-100'}`}>
                          <div
                            className={`h-3 rounded-full transition-all duration-500 ${resultado.ok ? 'bg-teal-600' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(100, Number(resultado.progreso.progreso) || 0)}%` }}
                          />
                        </div>
                        <p className={`text-xs mt-2 ${resultado.ok ? 'text-green-800' : 'text-red-700'}`}>
                          Creados: {resultado.progreso.exitosos || 0}
                          {resultado.progreso.fallidos ? ` · Con error: ${resultado.progreso.fallidos}` : ''}
                          {['procesando', 'pendiente', 'enviando'].includes(resultado.progreso.estado)
                            ? (resultado.progreso.estado === 'pendiente'
                                ? ' · En cola: asegúrese de tener corriendo php artisan queue:work --timeout=0'
                                : ' · La importación continúa en segundo plano; puede dejar esta ventana abierta.')
                            : ''}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {modoImportacion === 'regularizar' && mostrarValidacionesRegularizar && (
            <>
              <div className="mb-4 grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded p-3"><span className="text-lg font-bold text-gray-700">{datosRegularizar.length}</span><p className="text-xs text-gray-600">Total filas</p></div>
                <div className="bg-green-50 rounded p-3"><span className="text-lg font-bold text-green-700">{filasValidasRegularizar.length}</span><p className="text-xs text-green-700">Válidas</p></div>
                <div className="bg-red-50 rounded p-3"><span className="text-lg font-bold text-red-700">{erroresRegularizar.length}</span><p className="text-xs text-red-700">Con error</p></div>
              </div>

              {erroresRegularizar.length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg max-h-48 overflow-y-auto">
                  <h4 className="font-medium text-red-900 mb-2">Errores de validación</h4>
                  <ul className="text-sm text-red-800 space-y-1">
                    {erroresRegularizar.map((e, i) => (
                      <li key={i}>Fila {e.fila}: {e.nombre} — {e.mensaje}</li>
                    ))}
                  </ul>
                </div>
              )}

              {filasValidasRegularizar.length > 0 && erroresRegularizar.length === 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">Todas las filas son válidas. Los conceptos se añadirán a los tareos existentes.</p>
                </div>
              )}

              {filasValidasRegularizar.length > 0 && (
                <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Vista previa ({filasValidasRegularizar.length} registros)</h4>
                  <div className="max-h-64 overflow-auto border border-gray-200 rounded">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">#</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">DNI</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Empleado</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Fecha</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Concepto</th>
                          <th className="px-2 py-1.5 text-left font-medium text-gray-700">Monto</th>
                          <th className="px-2 py-1.5 text-left font-medium text-amber-800">Destino</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {filasValidasRegularizar.map((fila, idx) => {
                          const emp = employees.find(e => e.id === fila.empleado_id);
                          const conceptoStr = fila.conceptos?.length
                            ? fila.conceptos.map((c) => {
                                const plame = (plameConceptos || []).find(p => String(p.id) === String(c.plame_configuracion_id));
                                const label = plame ? (plame.codigo_plame || plame.codigo || plame.nombre || c.tipo_concepto) : c.tipo_concepto;
                                return `${label || '—'}`;
                              }).join('; ')
                            : '—';
                          const destinosStr = fila.conceptos?.length
                            ? [...new Set(fila.conceptos.map((c) => c.tipo_boleta || 'plame'))].join(', ')
                            : '—';
                          return (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-2 py-1 text-gray-600">{idx + 1}</td>
                              <td className="px-2 py-1 text-gray-800">{emp?.numero_documento ?? '—'}</td>
                              <td className="px-2 py-1 text-gray-800 max-w-[120px] truncate" title={emp?.nombre_completo}>{emp?.nombre_completo ?? '—'}</td>
                              <td className="px-2 py-1 text-gray-800">{fila.fecha}</td>
                              <td className="px-2 py-1 text-gray-600 max-w-[120px] truncate" title={conceptoStr}>{conceptoStr}</td>
                              <td className="px-2 py-1 text-gray-600">{fila.conceptos?.reduce((s, c) => s + (Number(c.monto) || 0), 0).toFixed(2)}</td>
                              <td className="px-2 py-1 text-amber-800 font-medium">{destinosStr}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {filasValidasRegularizar.length > 0 && (
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={ejecutarRegularizacion}
                    disabled={aplicandoRegularizar || erroresRegularizar.length > 0}
                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {aplicandoRegularizar ? 'Aplicando...' : 'Aplicar conceptos'}
                  </button>
                </div>
              )}
            </>
          )}

          {modoImportacion === 'importar' && resultado && !resultado.background && (
            <div className={`mt-6 p-4 rounded-lg ${resultado.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {resultado.ok ? (
                <>
                <p className="text-green-800">{resultado.message}</p>
                </>
              ) : (
                <>
                  <p className="text-red-800 font-medium">{resultado.message}</p>
                  {hayErroresActividad && (
                    <div className="mt-3 p-3 bg-red-100 rounded max-h-48 overflow-y-auto">
                      <p className="text-sm font-medium text-red-900 mb-2">Empleados no activos en la fecha indicada (revise historial laboral):</p>
                      <ul className="text-sm text-red-800 space-y-1">
                        {resultado.errores_actividad.map((e, i) => (
                          <li key={i}>Fila {e.fila}: {e.empleado_nombre ?? e.empleado_id} — Fecha {e.fecha} — {e.mensaje}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {hayErroresTipoEvento && (
                    <div className="mt-3 p-3 bg-red-100 rounded max-h-48 overflow-y-auto">
                      <p className="text-sm font-medium text-red-900 mb-2">Horas reales no permitidas para el tipo de evento (deben estar vacías para falta, feriado, descanso, permiso, vacaciones, etc.):</p>
                      <ul className="text-sm text-red-800 space-y-1">
                        {resultado.errores_tipo_evento.map((e, i) => (
                          <li key={i}>Fila {e.fila}: tipo {e.tipo_evento} — {e.mensaje}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {hayDuplicadosResultado && (
                    <div className="mt-3 p-3 bg-red-100 rounded max-h-48 overflow-y-auto">
                      <p className="text-sm font-medium text-red-900 mb-2">Tareos duplicados (mismo empleado, proyecto y fecha):</p>
                      <ul className="text-sm text-red-800 space-y-1">
                        {resultado.duplicados.slice(0, 40).map((e, i) => {
                          const emp = employees.find((em) => String(em.id) === String(e.empleado_id));
                          return (
                            <li key={i}>
                              Fila {e.fila ?? (e.index + 1)}: {emp?.nombre_completo || e.empleado_id} — {e.fecha}
                              {e.mensaje ? ` — ${e.mensaje}` : ''}
                            </li>
                          );
                        })}
                        {resultado.duplicados.length > 40 && (
                          <li>… y {resultado.duplicados.length - 40} más</li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {modoImportacion === 'importar' && resultado && resultado.background && !resultado.ok && (
            <div className="mt-6 p-4 rounded-lg bg-red-50 border border-red-200">
              <p className="text-red-800 font-medium">{resultado.message}</p>
              {hayErroresActividad && (
                <div className="mt-3 p-3 bg-red-100 rounded max-h-48 overflow-y-auto">
                  <p className="text-sm font-medium text-red-900 mb-2">Empleados no activos en la fecha indicada (revise historial laboral):</p>
                  <ul className="text-sm text-red-800 space-y-1">
                    {resultado.errores_actividad.map((e, i) => (
                      <li key={i}>Fila {e.fila}: {e.empleado_nombre ?? e.empleado_id} — Fecha {e.fecha} — {e.mensaje}</li>
                    ))}
                  </ul>
                </div>
              )}
              {hayErroresTipoEvento && (
                <div className="mt-3 p-3 bg-red-100 rounded max-h-48 overflow-y-auto">
                  <p className="text-sm font-medium text-red-900 mb-2">Horas reales no permitidas para el tipo de evento:</p>
                  <ul className="text-sm text-red-800 space-y-1">
                    {resultado.errores_tipo_evento.map((e, i) => (
                      <li key={i}>Fila {e.fila}: tipo {e.tipo_evento} — {e.mensaje}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {modoImportacion === 'regularizar' && resultadoRegularizar && (
            <div className={`mt-6 p-4 rounded-lg ${resultadoRegularizar.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              {resultadoRegularizar.ok ? (
                <p className="text-green-800">{resultadoRegularizar.message}</p>
              ) : (
                <p className="text-red-800 font-medium">{resultadoRegularizar.message}</p>
              )}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportadorTareos;
